import { imageToolHost } from './imageTools/host';
import { PicturePanelSection } from './imageTools/PicturePanel';
import { onPictureToolRequest } from './imageTools/request';
import { pageSummary } from './pageSummary';
import { askPage, worthIt } from './ai/pageSmarts';
import { ArtworkGeneration } from './ai/ArtworkGeneration';
import { selectedReferenceSources } from './ai/canvasReferences';
import { gatewayGenerationClient } from './ai/gateway';
import { tourGenerationClient, useFreeGeneration } from '../lib/tours/freeGeneration';
import { ANIMATABLE } from '../lib/tours/chapters';
import { emitHintSignal } from '@flobi/asset-library/hints';
import { canCombineSelection, geometrySelection, selectionMask } from './selectionGeometry';
import { blendSelection } from './blendSelection';
import { animationArtboard } from './activeArtboard';
import { applyMotionResize } from './shell/timingStripModel';
import { RulerUnits } from './shell/RulerUnits';
import { RULER_BAND } from './rulerLayout';
import { usePhone } from '../ui/usePhone';
import { PhoneToolTray } from './shell/PhoneToolTray';
import { PhoneSheet } from '../ui/phoneSheet';
import { HeldKeys } from './shell/HeldKeys';
import { Icon } from './chrome/icons';

/**
 * A phone keeps Layers and Animate in the title menu instead of the tool shelf and canvas corner.
 * Every tool stays in the dock (Mohamad, 2026-10-06: the light-edits-only
 * dock was "empty… add shapes and all the stuff I can do on the phone").
 */
const PHONE_HIDDEN = ['layers', 'animate'] as const;
/** What an online export is called on screen. */
/** The layers that hold others, whose colours are listed together in Properties. */
const COLOR_HOLDERS: ReadonlySet<string> = new Set(['group', 'blend', 'cloner', 'artboard']);
const CLOUD_NAMES = { pdf: 'PDF', jpg: 'JPEG', mp4: 'MP4', gif: 'GIF' } as const;
import { useAppTitle } from '@flobi/asset-library/app-shell';
import { duplicateFile } from '../home/api';
import { applyOps, assetsMap, columnsFor, MAGIC_STYLES, timingFor, toSvg, writeMeta, type NodeRecord } from '@flobi/artwork-core';
import { IS_MAC, keyBelongsToField, menuIsOpen, primaryKey, secondKey, typingIsOnCanvas } from '../ui/keys';
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSettings } from '@flobi/asset-library/account';
import { colorToHex, useTheme } from '../theme';
import { OPEN_ADDONS } from '../account/events';
import type { NewFileDefaults } from '../home/newFileDefaults';
import { ColourSourcesContext, hexFromTyped, type ColourSources, type NativeEyeDropper } from './chrome/Colour';
import { PaintImagesContext } from './shell/PaintImages';
import type { RepeatActions } from './shell/RepeatPanel';
import { artworkCmd } from '../services/apiClient';
import { ConfirmAlert } from '../ui/confirm';
import { type LayerAction } from './chrome/Layers';
import { boardSlotMoves } from './boardOrder';
import { FocusedArtboardPanel } from './chrome/FocusedArtboardPanel';
import { SaveTemplateDialog, type ArtboardTemplate } from './content/SaveTemplateDialog';
import { QuickMenu } from './chrome/QuickMenu';
import { canvasMenu } from './contextMenu';
import { addToAgentChat, setAgentSelection } from '@flobi/asset-library/agent';
import { layerItem } from './agentSelection';
import { clipboardItems, type ClipboardOptions } from './clipboardMenu';
import { ShortcutsSheet, shortcutGroups } from './shell/ShortcutsSheet';
import { useVersions } from './versions/useVersions';
import { versionHistoryMenu } from './versions/versionMenu';
import { NameVersionDialog, VersionPreview, VersionsPanel } from './versions/VersionsPanel';
import { copyVersion, renameVersion, restoreVersion, saveVersion, type FileVersion } from '../services/versions';
import { CommentPin, CommentsPanel } from './comments/CommentsPanel';
import { useComments } from './comments/useComments';
import { anchorAt, pinPoint } from './comments/anchor';
import { useWheelToCanvas } from './wheelToCanvas';
import type { CommentAnchor, CommentPerson } from '../services/comments';
import { gatewayCall } from '../share/gateway';
import { fetchLayerFile, readLayerFile } from '../layers/layerFile';
import { dropWords, toParcel, useDropTarget, type DroppedItem } from './dockDrag';
import { capSentence, overCap } from '../services/library';
import { readableMediaUrl } from '../services/mediaRead';
import { readClip } from '../canvas/clips';
import { facesFromParam, libraryFaces } from '../layers/fontParcel';
import { resolveLibraryFamily } from '../services/fonts';
import { placedWords, placeLayerFile } from '../layers/placeLayerFile';
import { fontsLine } from './imageTools/document';
import type { IconName } from './chrome/icons';
import { KINDS } from './tools/shapes';
import { useEditorCanvas, type ArtboardPreset, type ExportScope, type EditorStyle, type ToolId } from './useEditorCanvas';
import { fileNames, zip } from '../lib/zip';
import { useFlags } from './useFlags';
import { useViewSettings, type ViewKey, type ViewSettings } from './view';
import { ASSETS_WIDTH, LIBRARY_RAIL_WIDTH, Library, LibraryRail, useLibraryPlace } from './content/Library';
import type { LibraryActions } from './content/kinds';
import { useFollow, useNoticeToasts, type NoticeToast } from '@flobi/collab-client/react';
import { undoRun } from '../live/collab';
import { RemotePeople } from './shell/RemotePeople';
import { LOCAL_ORIGIN, UNDO_TAKEN_OVER, useEditorDocument } from './useEditorDocument';
import { AddonsContext, useAddons } from './addons/useAddons';
import { useCloudExport } from './cloudExport';
import { handOver, openExportPage } from './exportHandoff';
import { prepareVideoSnapshot } from './videoSnapshot';
import { exportTargets, sendExport, type SendApp } from './sendExport';
import { flowSourceOf, leftOutWords, sendArtboardToFlow } from './sendToFlow';
import { wholeOpenWith } from './wholeMenu';
import { useAppLogo } from '../lib/appDirectory';
import { NAVIGATION_WIDTH, NavigationPanel, type NavigationView } from './shell/NavigationPanel';
import { revealSelection } from './shell/revealSelection';
import type { MenuEntry } from '../ui/menu';
import { Button, Spinner } from '../ui/controls';
import { Toolbar, ALL_TOOLS, markOf, type PanelView, type ToolMark } from './shell/Toolbar';
import { SidePanel, PANEL_WIDTH, type PanelTab } from './shell/SidePanel';
import { PresentStage, presentTarget, presentUrl } from './shell/Presenting';
import { GenerationInfo } from './ai/GenerationInfo';
import { DocumentSettings } from './shell/PageInspector';
import { PressContext, useProofer } from './press';
import { useWorkOf, WorkContext } from './work';
import { hexOf, pickedInk, typedInk } from './ink';
import { AnimateBar, type Playback } from './shell/AnimateBar';
import type { LayerPicture } from './shell/AnimateThumbs';
import { playFrom, type Phase } from './shell/animateModel';
import type { ExportOptions, ExportCommand, ExportSize } from './shell/ExportPanel';
import { CreateEditDock, type CreateEditDockProps } from './shell/CreateEditDock';
import { SelectionActionsBar } from './shell/SelectionActionsBar';
import { ToolCursorGuide } from './shell/ToolCursorGuide';
import { GuidePeek } from './shell/GuidePeek';
import { useSeenGuides } from './guides/seen';
import { preloadToolCursors, toolCursor, toolFeedback } from './toolFeedback';
import { ToolStrip } from './shell/ToolStrip';
import type { DrawWith } from './shell/ToolOptions';
import type { SelectionActions } from './shell/SelectionInspector';
import type { RailButton } from './tools/types';
import { takeShareSectionFromUrl, useShareShortcut } from '@flobi/asset-library/share';
import { openFileShare } from '../share/artworkShare';
import { FileAccess } from '../share/FileAccess';
import { Notifications } from '../home/Notifications';
import { recolorWrites, selectionColors } from './selectionColors';
import { keepExportForTaste, TASTE_PICTURE_PX, TASTE_REUSE_BYTES } from '../services/tasteExport';
import { useTourActive } from '@flobi/asset-library/tours';
import { useGetStarted } from '../lib/tours/ArtworkGetStarted';
import { usePanelRefresh } from './panelRefresh';

/** Hands the file to the browser, which is the only way out of a page. */
function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  // Freed on the next turn of the loop, once the browser has taken it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Initial room for the header; Toolbar measures the actual rows after layout. */
const COARSE = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
/**
 * The suite's Ask button as the page finds it. The kit's launcher is `.skp-launcher` now (the round orb);
 * `.fa-launcher`, the old pill, is no longer drawn. A touch screen measures the real one (the responsive
 * pass, 2026-10-27); a laptop keeps its spacing as it was, unchanged.
 */
const LAUNCHER = '.skp-launcher'; // the round Agent orb, for every pointer since upstream's cf90cad
const TOP = COARSE ? 104 : 80;
/** Room for the zoom control when Animate is closed. */
const DOCK = COARSE ? 84 : 68;
/** The phone's tool tray, two rows of named tools with its handle, from the bottom edge. */
const PHONE_TRAY = 176;
/** A dropped picture's own size, when the drag did not say: read from the picture (0 × 0 when it cannot be read). */
const pictureSizeOf = (src: string) =>
  new Promise<{ width: number; height: number }>((resolve) => {
    const image = new Image();
    const done = () => resolve({ width: image.naturalWidth || 0, height: image.naturalHeight || 0 });
    image.onload = done;
    image.onerror = done;
    setTimeout(done, 10_000);
    image.src = src;
  });

/** The infinite canvas stays edge to edge beneath the tool shelf and independent panels. */
export function EditorPage({ onReopen }: { onReopen?: () => void } = {}) {
  useEffect(preloadToolCursors, []);
  const { documentId: linkedId = '' } = useParams<{ documentId: string }>();
  /**
   * In lower case, always: the id is a live room key, and a link typed with capitals
   * opened the right file in a room of its own — nobody's edits, cursors or renames
   * reached that tab, and its reached nobody (QA 2026-09-24, files-doc-F21). The address
   * bar is put right too, so a copied link is the canonical one.
   */
  const documentId = linkedId.toLowerCase();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (linkedId !== documentId) navigate(`/f/${documentId}`, { replace: true, state: location.state as unknown });
  }, [linkedId, documentId, navigate, location.state]);
  /**
   * The name the Files tile already showed, until the file's own arrives: the title read
   * "Untitled" for a second on every open, as if the wrong file had opened
   * (QA 2026-09-24, files-doc-F25).
   */
  const tileName = (location.state as { name?: unknown } | null)?.name;
  const knownName = typeof tileName === 'string' ? tileName : '';
  const { palette, theme } = useTheme();
  const settingsWindow = useSettings();
  const session = useEditorDocument(documentId);
  const cloud = useCloudExport(documentId);
  // The last online export sent on to Drive or Handoff (cross-app pages, 29 Oct): which is on its way.
  const [sendingExport, setSendingExport] = useState<SendApp | null>(null);
  const appLogo = useAppLogo(theme === 'light' ? 'light' : 'dark');
  const flags = useFlags();
  /**
   * A viewer (or a file that was deleted or taken away from this person): the server refuses
   * what they write, so the editor offers nothing to write with. By ref where the canvas and the
   * key handlers read it mid-frame; the chrome reads the value.
   */
  /** This tab is the full view Play opened: the file, read only, one artboard at a time. */
  const [presenting] = useState(() => presentTarget() !== null);
  const viewing = session.readOnly || session.stopped || presenting;
  const viewingRef = useRef(viewing);
  viewingRef.current = viewing;
  /** The tool in hand. A viewer's stays the arrow: nothing picks another. */
  const [panning, setPanning] = useState(false);
  const panRef = useRef(false);
  panRef.current = panning;
  const setTool: Dispatch<SetStateAction<ToolId>> = useCallback((next) => {
    setPanning(false);
    if (!viewingRef.current) setToolState(next);
  }, []);
  const [tool, setToolState] = useState<ToolId>('select');
  /** Which of the shapes the toolbar's shape mark wears: whichever one was last in hand. */
  const [shape, setShape] = useState<ToolId>('rect');
  /**
   * A tool changing its own settings is the one rail change React cannot see coming, so
   * the tool says so and this bumps a counter nobody reads: the rail is re-read during
   * the render it causes.
   */
  const [, bumpRail] = useState(0);
  const [cropping, setCropping] = useState<string | null>(null);
  /** Guides coming and going (view-snapping-F21). */
  const [, bumpGuides] = useState(0);
  /**
   * Properties and navigation occupy the sides; Animate has independent bottom space.
   */
  // Nothing opens by itself: Properties comes out on request (•••, ⌥⌘I, the top bar) and
  // Layers from the rail — the rule since 23 Sep, lost on 8 Oct, back on 15 Oct (the audit).
  const [panel, setPanel] = useState<'format' | null>(null);
  const [animating, setAnimating] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(TOP);
  // The zoom pill's slot, where Generate shows the bar for every run on the page.
  const [runsHost, setRunsHost] = useState<HTMLElement | null>(null);
  const [narrowWorkspace, setNarrowWorkspace] = useState(() => window.innerWidth <= 940);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 940px)');
    const change = () => {
      setNarrowWorkspace(query.matches);
      // Side panels become drawers on small screens. Keep only one open.
      if (query.matches) setNavigation(null);
    };
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, []);
  const [navigation, setNavigation] = useState<NavigationView | null>(null);
  /** A picture's Edit with AI: its tools, standing in the Generate slot of the library. */
  const [editAI, setEditAI] = useState(false);
  // The tool open in Edit with AI: the panel's header takes its name and a Back to the list.
  const [editScreen, setEditScreen] = useState<{ title: string; back: () => void } | null>(null);
  const toggleNavigation = useCallback((view: NavigationView) => {
    // Assets puts things on the page and Layers rearranges it: neither is a viewer's.
    if (viewingRef.current) return;
    if (window.innerWidth < 1100) setPanel(null);
    setNavigation((open) => (open === view ? null : view));
  }, []);
  // A tablet keeps one drawer at a time so the canvas still has usable room.
  useEffect(() => {
    const narrow = window.matchMedia('(max-width: 1099px)');
    const resize = () => {
      if (narrow.matches && panel !== null) setNavigation(null);
    };
    narrow.addEventListener('change', resize);
    return () => narrow.removeEventListener('change', resize);
  }, [panel]);
  /** Document Settings, hanging from the title while they are being changed. */
  const [settingsOpen, setSettingsOpen] = useState(false);
  // A hint's button (lib/hints): Document settings, as Document ▸ Document settings… opens it.
  useEffect(() => {
    const ask = (event: Event) => {
      if (viewingRef.current) return;
      setSettingsOpen(true);
      event.preventDefault();
    };
    window.addEventListener('artwork:document-settings', ask);
    return () => window.removeEventListener('artwork:document-settings', ask);
  }, []);
  /** The keyboard shortcuts sheet, opened with ? or View ▸ Keyboard shortcuts (W5-AR3). */
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const closeShortcuts = useCallback(() => setShortcutsOpen(false), []);
  /** Version history (W5-AR1): the versions, the one previewed, the name being typed, a restore under way. */
  const history = useVersions(documentId);
  const [chosenVersion, setChosenVersion] = useState<FileVersion | null>(null);
  const [naming, setNaming] = useState<{ version: FileVersion | null } | null>(null);
  const [templateDraft, setTemplateDraft] = useState<{ template: ArtboardTemplate; returnFocus: HTMLElement | null } | null>(null);
  const [restoring, setRestoring] = useState(false);
  const cancelNaming = useCallback(() => setNaming(null), []);
  /**
   * The Comment tool (C) opens discussion and drops pins; the selected thread shares
   * its selection with its pin, and a draft pin waits for its words.
   */
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [chosenThread, setChosenThread] = useState<string | null>(null);
  const [draftPin, setDraftPin] = useState<CommentAnchor | null>(null);
  const [commentPeople, setCommentPeople] = useState<CommentPerson[]>([]);
  const pickPicture = useRef<HTMLInputElement>(null);
  /** The file dialog Replace Image opens, and the layers the picture chosen in it goes into. */
  const pickReplacement = useRef<HTMLInputElement>(null);
  const replacing = useRef<readonly string[]>([]);
  const [style, setStyle] = useState<EditorStyle>({ fill: '#5f72a3', fillOn: true, stroke: '#111111', strokeWeight: 0, opacity: 1 });
  const [selection, setSelection] = useState<string[]>([]);
  const [zoom, setZoom] = useState(1);
  const layersOpen = !viewing && navigation === 'layers';
  /** The artboard grid: columns (unset follows the count) and the gap, remembered per file. */
  const gridKey = `artwork.board-grid.${documentId}`;
  const [grid, setGrid] = useState<{ columns: number | null; gap: number }>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(gridKey) ?? 'null') as { columns: number | null; gap: number } | null;
      if (saved && typeof saved.gap === 'number') return saved;
    } catch {
      // Nothing saved, or storage refused: the defaults.
    }
    return { columns: null, gap: 80 };
  });
  const keepGrid = (next: { columns: number | null; gap: number }) => {
    setGrid(next);
    try {
      localStorage.setItem(gridKey, JSON.stringify(next));
    } catch {
      // As above.
    }
  };
  const [sceneVersion, setSceneVersion] = useState(0);
  const panelRefresh = usePanelRefresh(() => setSceneVersion((v) => v + 1));
  const [bare, setBare] = useState(presenting);
  const [quickAt, setQuickAt] = useState<{ x: number; y: number; artboardId?: string | null } | null>(null);
  const { view, toggle: toggleView } = useViewSettings();
  /** Whose effects the layers list should open on, when it is opened from elsewhere. */
  const [effectsOn] = useState<string | null>(null);
  /**
   * Whether a page is playing, or held on one instant. The canvas owns the clock — a ref
   * change is not a render — so this is only what the button says, and the canvas puts it
   * back when it stops.
   */
  const [playback, setPlayback] = useState<Playback>('idle');
  /** Whether a page that plays to its end starts again. A way of watching, not part of the file. */
  const [looping, setLooping] = useState(false);
  const loopingRef = useRef(looping);
  loopingRef.current = looping;
  /** Whether the canvas is waiting for a drag that will become a motion path. */
  const [drawingPath, setDrawingPath] = useState(false);
  /** Whether the Animate bar's timeline is out. */
  const [timeline, setTimeline] = useState(false);
  /** Shared measured height keeps Animate and both side panels joined. */
  const [barHeight, setBarHeight] = useState(0);
  /** Whether a layer has been picked while animating, this visit: the bar's one line of help goes once it has. */
  const [pickedOnce, setPickedOnce] = useState(false);
  const library = !viewing && navigation === 'assets';
  /** Which of its sources it shows — set from outside too, when More asks for the add-ons. */
  const [libraryPlace, setLibraryPlace] = useLibraryPlace();
  // The generation controller stays mounted when its sidebar closes: drafts and runs survive.
  const [generationHost, setGenerationHost] = useState<HTMLDivElement | null>(null);
  const [generationReferenceRequest, setGenerationReferenceRequest] = useState<import('./ai/contracts').ReferenceIntent | null>(null);
  const openAddons = () => {
    if (viewingRef.current) return;
    setLibraryPlace('addons');
    if (window.innerWidth < 1100) setPanel(null);
    setNavigation('assets');
  };
  // The Settings window's Add-ons row asks for the panel, which lives in this drawer.
  const openAddonsRef = useRef(openAddons);
  openAddonsRef.current = openAddons;
  useEffect(() => {
    const ask = () => openAddonsRef.current();
    window.addEventListener(OPEN_ADDONS, ask);
    return () => window.removeEventListener(OPEN_ADDONS, ask);
  }, []);
  // A hint's button (lib/hints): Assets opened on one of its places — My Stuff, the Brand tab.
  // Handled, the event is cancelled, which is how the hint knows its button did something.
  const openLibraryAt = useRef<(place: string) => boolean>(() => false);
  openLibraryAt.current = (place: string) => {
    if (viewingRef.current) return false;
    setLibraryPlace(place);
    if (window.innerWidth < 1100) setPanel(null);
    setNavigation('assets');
    return true;
  };
  useEffect(() => {
    const ask = (event: Event) => {
      const place = (event as CustomEvent<{ place?: string }>).detail?.place;
      if (place && openLibraryAt.current(place)) event.preventDefault();
    };
    window.addEventListener('artwork:library', ask);
    return () => window.removeEventListener('artwork:library', ask);
  }, []);
  /** A message that has something to say for a moment: an export that finished. */
  const [toast, setToastText] = useState<string | null>(null);
  /** What the toast offers besides Close: Undo, on a notice about an AI run (CONTRACTS §12). */
  const [noticeAction, setNoticeAction] = useState<NoticeToast['action'] | null>(null);
  const setToast = useCallback((message: string | null) => {
    setToastText(message);
    setNoticeAction(null);
  }, []);
  const togglePanel = useCallback((view: PanelView) => {
    // Properties and Animate are for changing things; a viewer has neither.
    if (viewingRef.current) return;
    if (view === 'animate') {
      if (animating) {
        setAnimating(false);
        return;
      }
      // A blank artboard click sets the outlined editing context with no selected
      // objects. Reuse that exact board; explicit loose/mixed selections stay guarded.
      const activeBoard = selection.length === 0 ? canvasRef.current?.exportArtboard() ?? null : null;
      if (!animationArtboard(selection, id => session.open?.scene.get(id), activeBoard)) {
        setToast('Select an artboard to animate.');
        return;
      }
      setToast(null);
      setAnimating(true);
      return;
    }
    if (window.innerWidth < 1100) setNavigation(null);
    const opening = panel !== 'format';
    // Properties pressed while the Edit tab shows goes to Properties, rather than closing the panel.
    if (!opening && pictureTabShown.current) { setPanelTab('properties'); return; }
    setPanel(opening ? 'format' : null);
    if (opening) setPanelTab('properties');
    // Comments is the other tab of the same place: Properties out puts it away.
    if (opening) {
      setCommentsOpen(false);
      setCommenting(false);
    }
  }, [animating, panel, selection, session.open, setToast]);
  /** What the server says to this person — a repair, a restore, an AI run — in the same toast. */
  const showNotice = useCallback((notice: NoticeToast) => {
    setToastText(notice.text);
    setNoticeAction(notice.action ?? null);
  }, []);
  /** The file's project and the brand it is made in, for the panel, the colour picker and the text presets. */
  // A press of undo whose step someone else had since changed changes nothing: say why (§21.41).
  const undoTakenOver = session.undoTakenOver;
  useEffect(() => {
    if (undoTakenOver > 0) setToast(UNDO_TAKEN_OVER);
  }, [undoTakenOver, setToast]);
  const work = useWorkOf(documentId, session.open !== null, setToast);
  // Read again whenever Assets is opened: a brand is changed in another app, and nothing tells this one.
  const refreshWork = work.refresh;
  useEffect(() => {
    if (library) refreshWork();
  }, [library, refreshWork]);
  /** A rename the server accepted, until the file's own name catches up (files-doc-F10). */
  const [renamed, setRenamed] = useState<{ from: string; to: string } | null>(null);
  /** Asking before the file is deleted (files-doc-F4). */
  const [deleting, setDeleting] = useState(false);
  /** Try Again, when the canvas could not start (files-doc-F8). */
  const [startFailed, setStartFailed] = useState<(() => void) | null>(null);
  /** Pictures still on their way up (files-doc-F11, F23). */
  const [uploading, setUploading] = useState(0);
  /** A phone-sized window: the top bar keeps only what fits (`PHONE_HIDDEN`). */
  const phone = usePhone();
  /** How many set off with them, to say how far a big drop has got. */
  const [uploadingOf, setUploadingOf] = useState(0);
  // Leaving mid-upload loses the picture, so the browser asks first (files-doc-F23).
  useEffect(() => {
    if (uploading === 0) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [uploading]);
  /** The file's name, or the one its tile showed until the file's own arrives (files-doc-F25). */
  const shownName = session.name || knownName;
  /** What the title says: a rename just made, until the file's own name moves on. */
  const titleName = renamed && renamed.from === session.name ? renamed.to : shownName;
  // The tab says which file it is, not "Artwork" for every one (QA 2026-09-24, files-doc-F27):
  // "Poster · Artwork", under the app's registry name, the way every Flobi app titles a page.
  useAppTitle(titleName);

  const toolRef = useRef(tool);
  toolRef.current = tool;
  const styleRef = useRef(style);
  styleRef.current = style;
  // The document's unit reaches the canvas by ref, like the tool does: it changes rarely
  // and every readout on the overlay has to be able to ask for it mid-frame.
  const unitsRef = useRef(session.units);
  unitsRef.current = session.units;
  // Where an upload is kept in Drive: this project's folder, when the file is in one this person can reach.
  const projectRef = useRef<string | null>(null);
  projectRef.current = work.context?.project?.id ?? null;
  const textEnabledRef = useRef(flags.isEnabled('artwork.text'));
  textEnabledRef.current = flags.isEnabled('artwork.text');
  const viewRef = useRef<ViewSettings>(view);
  viewRef.current = view;
  /**
   * Where the rulers start and what Fit centres inside: past the toolbar's band and the
   * side panel. In full screen there is no chrome, so the rulers go back to the window's
   * own edge.
   */
  const insetRef = useRef<{ top: number; left: number; right?: number; bottom?: number; reserve?: number; rulers?: { top: number; bottom: number } }>({ top: 0, left: 0 });
  /** The press, in a CMYK file: every well and picker shows colours through it. */
  const proofer = useProofer(session.colorMode);
  const prooferRef = useRef(proofer);
  prooferRef.current = proofer;

  /**
   * What the colour picker reads from the file: its colours, and the canvas's eyedropper
   * for a browser with none of its own. By ref through the handle, so the value is stable.
   */
  const canvasRef = useRef<ReturnType<typeof useEditorCanvas> | null>(null);
  const colourSources = useMemo<ColourSources>(
    () => ({
      fileColours: () => canvasRef.current?.fileColours() ?? [],
      eyedrop: (done) => canvasRef.current?.pickColour((color) => done(colorToHex(color))),
    }),
    [],
  );

  const canvas = useEditorCanvas({
    open: session.open,
    palette,
    toolRef,
    panRef,
    styleRef,
    unitsRef,
    projectRef,
    textEnabledRef,
    viewRef,
    insetRef,
    onSelection: setSelection,
    readOnlyRef: viewingRef,
    onZoom: setZoom,
    // Clear Guides reads the count as it renders, so a change in it must render.
    onGuidesChanged: () => bumpGuides((tick) => tick + 1),
    // Procreate returns to the previous tool once a shape is drawn.
    onToolDone: () => setTool('select'),
    onNotice: (message: string) => setToast(message),
    onUploading: (count: number, of: number) => {
      setUploading(count);
      setUploadingOf(of);
    },
    onStartFailed: (retry: (() => void) | null) => setStartFailed(retry ? () => retry : null),
    onQuickMenu: at => setQuickAt(at),
    onFullScreen: () => {
      setBare((on) => presenting || !on);
      setQuickAt(null);
    },
    onPickedColour: (color) => {
      // In a CMYK file the colour taken is an ink: the one it was typed as, else the one
      // the press makes of it.
      const press = prooferRef.current;
      const taken = press ? (color.cmyk ? typedInk(press, color.cmyk) : pickedInk(press, color)) : color;
      setStyle((current) => ({ ...current, fill: hexOf(taken), fillInk: taken.cmyk, fillOn: true }));
    },
    // A tool's option sets what the tool draws with; it does not repaint what is chosen.
    onDrawWith: (patch) => drawWith(patch),
    onRailChanged: () => bumpRail((tick) => tick + 1),
    onCroppingChanged: setCropping,
    onChooseTool: (id) => setTool(id),
    onShowFields: () => {
      if (window.innerWidth < 1100) setNavigation(null);
      setPanel('format');
    },
    // The play mark outside an animated board: the same playing as Animate's Play, so the
    // bar, when it is open, shows it playing.
    onPlayPage: (id) => play(id),
  });
  canvasRef.current = canvas;
  /**
   * Media dragged in from the app docked beside Artwork (the kit's dock drag, 2 Nov 2026) goes onto THIS file, as a My
   * Stuff tile let go there goes: a picture centred where it was dropped, on the page under it (else the middle of the
   * page being worked on), each next one a step down and across; a clip read for its length and size and held to the
   * cap first. By its media asset only, never a re-upload: a thing with none is left out, and the toast says so. Words
   * arrive as a text parcel does, editable text on a new artboard. The toast says what happened, so the kit stays quiet.
   */
  useDropTarget({
    name: titleName,
    takes: ['picture', 'video', 'text'],
    ref: canvas.canvasRef,
    // A file that was refused before it opened has nothing open: the Open-with page takes the drop.
    enabled: Boolean(session.open) || !session.problem,
    blocked: !session.open
      ? 'This file is still opening.'
      : presenting
        ? 'Stop presenting to add to this file.'
        : session.stopped
          ? session.problem || 'This file is view only.'
          : viewing
            ? 'This file is view only.'
            : false,
    onDrop: async (items, at) => {
      const centre = at.inside && at.x != null && at.y != null ? canvas.worldAt({ x: at.x, y: at.y }) : null;
      const placedItems: DroppedItem[] = [];
      const lines: string[] = [];
      const spot = () => (centre ? { x: centre.x + 24 * placedItems.length, y: centre.y + 24 * placedItems.length } : undefined);
      for (const it of items) {
        if (it.kind !== 'picture' && it.kind !== 'video') continue;
        const name = it.name || (it.kind === 'video' ? 'Clip' : 'Picture');
        const id = it.mediaAssetId;
        if (!id || !it.url) {
          lines.push(`${name} isn't a file in Flobi yet, so it was left out.`);
          continue;
        }
        const mime = it.mime ? { mime: it.mime } : {};
        try {
          if (it.kind === 'video') {
            const held = overCap({ kind: 'video', bytes: it.size, duration: it.duration });
            if (held) {
              lines.push(capSentence(name, held));
              continue;
            }
            // How long it runs is in the file: read before it is placed, as a My Stuff clip is.
            const facts = await readClip(await readableMediaUrl(it.url, id));
            const tooLong = overCap({ kind: 'video', duration: facts.duration });
            if (tooLong) {
              lines.push(capSentence(name, tooLong));
              continue;
            }
            (canvasRef.current ?? canvas).addPicture({ assetId: id, mediaAssetId: id, name, src: it.url, width: facts.width, height: facts.height, ...mime, duration: Math.round(facts.duration * 1000) / 1000 }, spot());
          } else {
            const size = it.width && it.height ? { width: it.width, height: it.height } : await pictureSizeOf(it.url);
            (canvasRef.current ?? canvas).addPicture({ assetId: id, mediaAssetId: id, name, src: it.url, ...size, ...mime }, spot());
          }
          placedItems.push(it);
          emitHintSignal('asset.placed', { kind: 'picture' });
        } catch (error) {
          lines.push(`${name} could not be opened: ${(error as Error).message}.`);
        }
      }
      const words = items.filter((it) => it.kind === 'text');
      if (words.length) {
        try {
          const file = readLayerFile({ ...toParcel(words, { name: titleName, from: at.from }), kind: 'flobi.parcel', type: 'text' });
          const { swapped } = await placeLayerFile(canvasRef.current ?? canvas, file, documentId);
          placedItems.push(...words);
          lines.push(['The words are on a new artboard, with the text editable.', fontsLine(swapped)].filter(Boolean).join(' '));
        } catch (error) {
          lines.push((error as Error).message || 'The words could not be placed.');
        }
      }
      const added = placedItems.length ? `Added ${dropWords(placedItems)} to ${titleName || 'this file'}.` : '';
      setToast([added, ...lines].filter(Boolean).join(' ') || null);
      return { quiet: true };
    },
  });

  /**
   * The eyedropper on what is chosen (Farah, 23 Oct: "I select a color from anything on my
   * screen and the element I selected turns into that color"). The browser's own takes it
   * from anywhere on the screen and Escape lets it go; without one, the canvas's next press.
   * The colour goes where a brand colour would (`paintSelection`), as one undo step.
   */
  const eyedropSelection = useCallback((to?: 'stroke') => {
    emitHintSignal('colour.eyedropped');
    const paint = (hex: string) => canvasRef.current?.paintSelection(hex, to);
    const native = (window as { EyeDropper?: NativeEyeDropper }).EyeDropper;
    if (native) {
      new native()
        .open()
        .then((taken) => {
          const hex = hexFromTyped(taken.sRGBHex);
          if (hex) paint(hex);
        })
        // Escape while picking is not an error.
        .catch(() => undefined);
    } else canvasRef.current?.pickColour((color) => paint(colorToHex(color)));
  }, []);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  // The Animate tour's "Press Animate" (lib/tours ANIMATABLE): a choice Animate can't take is let go of, so the press opens the whole page.
  useEffect(() => {
    const letGo = () => {
      const chosen = selectionRef.current;
      if (chosen.length && !animationArtboard(chosen, id => session.open?.scene.get(id), null)) canvas.select([]);
    };
    window.addEventListener(ANIMATABLE, letGo);
    return () => window.removeEventListener(ANIMATABLE, letGo);
  }, [canvas, session.open]);
  // In the AI tour the Generate panel's pictures are free (lib/tours/freeGeneration); Edit with AI's tools stay real.
  const freeGeneration = useFreeGeneration();
  const realGenerationClient = useMemo(() => gatewayGenerationClient(documentId, projectRef.current), [documentId, projectRef.current, gatewayGenerationClient]);
  const generationClient = useMemo(() => tourGenerationClient(realGenerationClient, freeGeneration), [realGenerationClient, freeGeneration]);
  // Edit › Tools on a picture or clip (imageTools): Adjust and Blur live, the AI tools as runs on the layer.
  // The side panel's tab when a picture or clip is chosen: Properties, or Edit (its tools — the bar's Edit opens it).
  const [panelTab, setPanelTab] = useState<PanelTab>('properties');
  /** Which of a picture's own tabs (Edit, Info) is showing in the panel now, if either. */
  const pictureTabShown = useRef<PanelTab | null>(null);
  const imageTools = useMemo(() => session.open ? imageToolHost({ open: session.open, canvas, client: realGenerationClient, notice: setToast,
    toVideo: id => { setGenerationReferenceRequest({ key: crypto.randomUUID(), ids: [id], kind: 'video' }); openLibraryAt.current('generate'); } }) : null,
  [session.open, canvas, realGenerationClient]);
  // A picture tool asked for from the Generate panel's suggestion (imageTools/request.ts): the picture
  // is chosen and the side panel's Edit tab opens, where the picture panel opens that tool.
  useEffect(() => onPictureToolRequest(({ id }) => { canvas.select([id]); setLibraryPlace('generate'); setNavigation('assets'); setEditAI(true); }), [canvas, setLibraryPlace]);

  // ── the others in the file ────────────────────────────────────────────────
  const pageRoom = session.open?.pageRoom ?? null;
  const rootRoom = session.open?.rootRoom ?? null;
  /** The view as Follow last set it, so only the person's own pan or zoom ends it. */
  const followedView = useRef('');
  const applyingFollow = useRef(false);
  const follow = useFollow(pageRoom, {
    onView: (view) => {
      applyingFollow.current = true;
      try {
        canvas.goTo(view);
        followedView.current = canvas.viewKey();
      } finally {
        applyingFollow.current = false;
      }
    },
  });
  const followRef = useRef(follow);
  followRef.current = follow;
  useEffect(
    () =>
      canvas.onViewChange(() => {
        const key = canvas.viewKey();
        if (key === followedView.current) return;
        followedView.current = key;
        if (!applyingFollow.current && followRef.current.followingId) followRef.current.stop();
      }),
    [canvas],
  );
  // What the server tells this person: a repair that changed their work, a restore, an AI run (with Undo).
  useNoticeToasts(pageRoom, showNotice, {
    undoRun: (runId) => (pageRoom ? undoRun(pageRoom.name, runId) : undefined),
    onUndoError: () => setToast('That change could not be undone.'),
  });
  useNoticeToasts(rootRoom, showNotice, {
    undoRun: (runId) => (rootRoom ? undoRun(rootRoom.name, runId) : undefined),
    onUndoError: () => setToast('That change could not be undone.'),
  });

  /** Add-ons: the list, the one open, and whichever tool is in the slot. */
  // A tool's problems are said in the toast, which shows with the Library closed
  // (QA 2026-09-24, library-addons-F5).
  const addons = useAddons(canvas, setTool, session.open !== null, tool, setToast);

  /**
   * The Select tool's rail says how it is pointing: at whole shapes (the ordinary state,
   * which needs nothing on screen) or, with a path's points open, at those points.
   */
  const rail = canvas.rail();
  const textModes = canvas.rail('text')?.top;
  const selectionModes = canvas.rail('select')?.top;
  const feedback = toolFeedback(tool, rail);
  /**
   * A tool (or mode) picked for the first time in this person's life shows its guide on
   * its own; after that it waits for Option or a long hover. The tool in hand when the
   * editor opens was not picked, so it is not introduced.
   */
  const seenGuides = useSeenGuides();
  // A running tour has its own cards: the tools' guides wait (lib/tours).
  const touring = useTourActive();
  const getStarted = useGetStarted();
  const [introduce, setIntroduce] = useState<{ key: string; at: number } | null>(null);
  const openedWith = useRef<string | null>(null);
  useEffect(() => {
    if (openedWith.current === null) {
      openedWith.current = feedback.key;
      return;
    }
    if (seenGuides.has(feedback.key)) return;
    seenGuides.mark(feedback.key);
    setIntroduce({ key: feedback.key, at: performance.now() });
  }, [feedback.key, seenGuides]);
  const pointing = tool === 'select' && rail?.top?.kind === 'buttons' ? rail.top.items.find((item) => item.pressed)?.id : undefined;
  const editingPoints = pointing !== undefined && pointing !== 'object';
  /** The shelf's second row: the active tool's options, or Edit Points. */
  const stripRail = tool !== 'select' ? (rail && (rail.top || rail.middle || rail.bottom || rail.style) ? rail : null) : editingPoints ? rail : null;
  /** The inspector follows the selection, active tool, or current page until closed. */
  const formatOut = !viewing && panel === 'format';
  const panelOut = !bare && formatOut;
  /** How far the side panel reaches in from the trailing edge, which the canvas fits inside. */
  const commentsOut = !bare && commentsOpen && !panelOut;
  const trailing = !narrowWorkspace && (panelOut || commentsOut) ? PANEL_WIDTH : 0;
  // Closing the content gives the canvas room back; the category rail stays reserved.
  // A phone has no rail: Assets, Layers and Versions are sheets over the tray (Artwork A).
  const leading = !bare && !viewing && !phone ? (!narrowWorkspace && navigation ? (navigation === 'versions' ? LIBRARY_RAIL_WIDTH + NAVIGATION_WIDTH : ASSETS_WIDTH) : LIBRARY_RAIL_WIDTH) : 0;
  /** Presets and timing reserve exactly the space occupied by the dock. */
  const layerPicked = animating && selection.length > 0 && canvas.animationTarget()?.layer != null;
  const animateBottom = animating && !bare && barHeight > 0 ? barHeight + 16 : null;
  // The suite's Agent launcher is fixed outside the workspace, so the root carries what it
  // needs to sit beside the zoom pill: the Animate bar's height and the trailing panel.
  const cornerLift = animating && !bare && barHeight > 0 ? barHeight : 0;
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--artwork-corner-lift', `${cornerLift}px`);
    root.setProperty('--artwork-corner-trailing', `${trailing}px`);
    return () => {
      root.removeProperty('--artwork-corner-lift');
      root.removeProperty('--artwork-corner-trailing');
    };
  }, [cornerLift, trailing]);
  // The launcher's size too, so Animate stands beside it with one gap and on its middle.
  useEffect(() => {
    const root = document.documentElement.style;
    let watcher: ResizeObserver | null = null;
    let retry = 0;
    const find = () => {
      const launcher = document.querySelector<HTMLElement>(LAUNCHER);
      if (!launcher) {
        if (retry++ < 20) retry_timer = window.setTimeout(find, 250);
        return;
      }
      const measure = () => {
        root.setProperty('--artwork-agent-width', `${launcher.offsetWidth}px`);
        root.setProperty('--artwork-agent-height', `${launcher.offsetHeight}px`);
      };
      measure();
      if (typeof ResizeObserver !== 'undefined') {
        watcher = new ResizeObserver(measure);
        watcher.observe(launcher);
      }
    };
    let retry_timer = 0;
    find();
    return () => {
      window.clearTimeout(retry_timer);
      watcher?.disconnect();
      root.removeProperty('--artwork-agent-width');
      root.removeProperty('--artwork-agent-height');
    };
  }, []);
  // Rulers on = a band of chrome under the header; the context bar and the page's room
  // step down by it, the zoom pill steps right of its vertical twin.
  const rulerBand = view.rulers && !phone && !viewing ? RULER_BAND : 0;
  // The bottom row — the zoom pill, the dock, the Animate and Agent pills — shares one line.
  // When the dock, centred, would run into the zoom pill, the pill folds its controls (pan,
  // zoom, Fit all) and keeps only a run's bar; if the dock still has no room it is centred in
  // what is left between the pill and the corner pills (Tarek, 15 Oct: "just remove the +-
  // and the zoom and fit — keep generating working if it's working").
  const [bottomRow, setBottomRow] = useState({ folded: false, left: 0, right: 0 });
  const bottomRowRef = useRef(bottomRow); bottomRowRef.current = bottomRow;
  const zoomPillFull = useRef(0);
  useEffect(() => {
    if (phone || bare) return;
    const root = document.querySelector<HTMLElement>('.editor-workspace');
    if (!root) return;
    const GAP = 12;
    const measure = () => {
      const width = root.clientWidth - leading - trailing - 2 * GAP;
      const widthOf = (selector: string, from: ParentNode = root) => from.querySelector<HTMLElement>(selector)?.getBoundingClientRect().width ?? 0;
      const pill = widthOf('.workspace-canvas-nav'), animate = widthOf('.workspace-canvas-animate'), agent = widthOf(LAUNCHER, document), dock = widthOf('.create-edit-dock');
      if (!dock) return;
      // The pill's whole width is remembered while it shows, so folding it never argues with itself.
      if (!bottomRowRef.current.folded) zoomPillFull.current = pill;
      const corners = animate + agent + (animate && agent ? 12 : 0);
      const centred = (width - dock) / 2;
      const clearOf = (left: number) => centred >= left + GAP && centred + dock <= width - corners - GAP;
      const next = clearOf(zoomPillFull.current) ? { folded: false, left: 0, right: 0 }
        : clearOf(pill) && bottomRowRef.current.folded ? { folded: true, left: 0, right: 0 }
        : { folded: true, left: bottomRowRef.current.folded ? pill + GAP : 0, right: bottomRowRef.current.folded ? corners + GAP : 0 };
      setBottomRow(was => was.folded === next.folded && was.left === next.left && was.right === next.right ? was : next);
    };
    measure();
    const watcher = new ResizeObserver(measure);
    watcher.observe(root);
    for (const selector of ['.canvas-nav-controls', '.workspace-canvas-animate', '.create-edit-dock']) { const element = root.querySelector(selector); if (element) watcher.observe(element); }
    const launcher = document.querySelector(LAUNCHER); if (launcher) watcher.observe(launcher);
    return () => watcher.disconnect();
  }, [phone, bare, viewing, leading, trailing, animating]);
  const dockRoomBottom = DOCK;
  insetRef.current = bare
    ? { top: 0, left: 0, right: 0, bottom: 0 }
    : { top: headerHeight + rulerBand, left: leading, right: trailing, bottom: animateBottom ?? (phone ? PHONE_TRAY : dockRoomBottom), reserve: 0, rulers: { top: headerHeight, bottom: cornerLift } };

  // Opening or closing the panel changes the room the drawing has; the canvas is told.
  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
    /**
     * And what is chosen is brought out from under it. A shape near the right edge was
     * covered by the panel, the quick bar went with it, and that bar held the only button
     * on screen that closes the panel (QA 2026-09-24, menus-shortcuts-quickbar-F23).
     * Moved only as far as it takes, never past the leading edge.
     */
    if (trailing === 0 && leading === 0) return;
    const place = canvas.selectionPlace();
    if (!place) return;
    const shift = revealSelection(place.box, { left: leading + 16, right: window.innerWidth - trailing - 16 });
    if (shift !== 0) canvas.panBy(shift, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leading, trailing, headerHeight]);

  // Animate closing takes its playing, a move
  // being tried and a waiting Draw the Path with it: its bar was the only way to end any
  // of them (QA 2026-09-24, anim-F6, anim-F7).
  // Full screen only hides the bar, so a page may go on playing there.
  // Opening, closing, or resizing Animate preserves the camera; fitting stays explicit.
  useEffect(() => {
    if (animating) return;
    canvas.cancelPath();
    canvas.preview('', null);
    // The playhead goes back to the start: it is where Animate was left, not the file's.
    canvas.forgetPlayhead();
  }, [animating, canvas]);
  // Choosing a drawing tool cancels a pending motion-path gesture, but leaves Animate open.
  useEffect(() => {
    if (tool !== 'select') canvas.cancelPath();
  }, [tool, canvas]);
  // The bar's one line of help — that anything on the page can be picked — is shown until
  // somebody does it, once in their life, as the tool guides are.
  useEffect(() => {
    if (!layerPicked) return;
    setPickedOnce(true);
    seenGuides.mark('animate.pick');
  }, [layerPicked, seenGuides]);

  /**
   * Plays a page, keeping the Animate bar in step with the canvas's own clock. The canvas
   * calls back when it stops, so the two can never disagree about what is playing. A page
   * that reaches its end starts again while Play in a Loop is on.
   */
  const stopped = (id: string) => (finished: boolean) => {
    if (finished && loopingRef.current && canvas.play(id, stopped(id))) return;
    setPlayback('idle');
  };
  const play = (id: string, from = 0) => {
    if (canvas.play(id, stopped(id), from)) setPlayback('playing');
  };
  /** Holds a page on one instant: paused, or the playhead dragged there. */
  const hold = (id: string, seconds: number) => {
    if (canvas.seek(id, seconds, stopped(id))) setPlayback('held');
  };
  /**
   * Play, or pause: the Play button and the space bar. Playing starts from where the
   * playhead is — held there, or left there when the page was last stopped — and from the
   * start once it has reached the end. Pausing holds the page on the instant it had reached.
   */
  const togglePlay = () => {
    const target = canvas.animationTarget();
    if (!target) return;
    const at = canvas.playhead();
    if (at && !at.held && !at.resting) hold(at.id, at.seconds);
    else play(target.page.id, at && at.id === target.page.id ? at.seconds : 0);
  };
  const togglePlayRef = useRef(togglePlay);
  togglePlayRef.current = togglePlay;
  /**
   * Space plays and pauses while Animate is on — a TAP of it. Held, it is still the hand
   * that pans the page, so a press that moved the pointer, or lasted, is left to the canvas.
   */
  useEffect(() => {
    if (!animating) return;
    let pressed = 0;
    let used = false;
    const typing = (e: KeyboardEvent) => menuIsOpen() || typingIsOnCanvas() || keyBelongsToField(e.target, e.key);
    const down = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.metaKey || e.ctrlKey || e.altKey || typing(e)) return;
      // A button that was just pressed keeps the keyboard, and Space would press it again.
      if (e.target instanceof HTMLElement && e.target.closest('button, [role="tab"], [role="radio"], [role="slider"]')) {
        e.preventDefault();
        e.target.blur();
      }
      if (e.repeat) return;
      pressed = performance.now();
      used = false;
    };
    const dragged = () => {
      if (pressed) used = true;
    };
    const up = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || !pressed) return;
      const tapped = !used && performance.now() - pressed < 400;
      pressed = 0;
      if (!tapped || typing(e)) return;
      e.preventDefault();
      togglePlayRef.current();
    };
    window.addEventListener('keydown', down, true);
    window.addEventListener('keyup', up, true);
    window.addEventListener('pointerdown', dragged, true);
    return () => {
      window.removeEventListener('keydown', down, true);
      window.removeEventListener('keyup', up, true);
      window.removeEventListener('pointerdown', dragged, true);
    };
  }, [animating]);
  /** Plays a page from just before one layer comes in, keeps moving or goes: what was just changed. */
  const watch = (pageId: string, layerId: string, phase: Phase) => {
    const row = canvas.timelineOf(pageId)?.children.find((one) => one.id === layerId);
    play(pageId, playFrom(phase, row));
  };

  // The toolbar's shape mark follows whichever kind is in hand, however it got there.
  useEffect(() => {
    if (KINDS.some((item) => item.id === tool)) setShape(tool);
  }, [tool]);

  // The layers list and the side panel re-read the page whenever it changes — at most once
  // a frame, and while a hand is dragging on the canvas a few times a second. A drag
  // writes the page on every pointer move, and re-rendering the whole editor (Layers, the
  // side panel, the bars) on each one is what made moving and resizing artboards lag
  // (Tarek, 2026-11-02): the canvas draws itself and never waits for React. The last
  // change of a drag is always shown the moment the hand lets go.
  useEffect(() => {
    const scene = session.open?.scene;
    if (!scene) return;
    const stop = scene.subscribe(() => panelRefresh.schedule());
    return () => stop();
  }, [session.open, panelRefresh]);

  const undoState = useUndoState(session.open?.undo ?? null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // An open menu owns the keyboard; a field owns only the keys it types or moves with.
      // An open menu owns the keyboard, and so does text being typed on the canvas: its
      // letters are words, not tool keys.
      if (menuIsOpen() || typingIsOnCanvas()) return;
      const typing = keyBelongsToField(e.target, e.key);
      // ⌥⌘I, the platform's own key for the inspector.
      // On Windows ⌘ is Ctrl, and ⌃⌘ is Ctrl+Alt (`keysFor` says them that way): these
      // asked for the Windows key, so none of them could be pressed there (QA 2026-10-05,
      // ART-DCK-06, ART-EXP-01).
      // Ctrl+Alt is AltGr on many Windows keyboards, which TYPES (ś, í, ß): in a field, or
      // with AltGr itself held, the key is a letter and never one of these (review E1).
      const altGraph = !IS_MAC && (typing || e.getModifierState?.('AltGraph') === true);
      if (!altGraph && primaryKey(e) && e.altKey && !e.shiftKey && (e.code === 'KeyI' || e.key.toLowerCase() === 'i')) {
        e.preventDefault();
        togglePanel('format');
        return;
      }
      // ⌃⌘S, the platform's key for showing and hiding the sidebar: here, the library.
      // By the physical key, since Ctrl+Alt is AltGr on many keyboards and changes the letter.
      if (!altGraph && primaryKey(e) && secondKey(e) && !e.shiftKey && (e.code === 'KeyS' || e.key.toLowerCase() === 's')) {
        e.preventDefault();
        toggleNavigation('assets');
        return;
      }
      // ⇧⌘E opens Export, as File ▸ Export does.
      if (primaryKey(e) && e.shiftKey && !e.altKey && (e.code === 'KeyE' || e.key.toLowerCase() === 'e')) {
        e.preventDefault();
        window.dispatchEvent(new Event('artwork:export'));
        return;
      }
      // The canvas's own field takes keys while someone is typing into a text layer.
      if (e.metaKey || e.ctrlKey || typing) return;
      if (e.key === '?') {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }
      const key = e.key.toLowerCase();
      if (key === 'v') setTool('select');
      if (key === 'p') setTool('pen');
      if (key === 'd') setTool('draw');
      if (key === 't' && flags.isEnabled('artwork.text')) setTool('text');
      // Shift picks the second tool on a key, as Illustrator does: M is the rectangle and
      // shift-M is the shape builder, which is the one that takes rectangles apart.
      if (key === 'm') {
        if (e.shiftKey) setTool('builder');
        else {
          setShape('rect');
          setTool('rect');
        }
      }
      // W is the blend and shift-R the liquify brush, where Illustrator puts them.
      if (key === 'w') setTool('blend');
      if (key === 'e') setTool('cut');
      if (key === 'r' && !e.altKey) setTool(e.shiftKey ? 'liquify' : 'warp');
      // K is the slice and shift-K the measure: both ask questions OF the page.
      if (key === 'k') setTool(e.shiftKey ? 'measure' : 'slice');
      if (key === 'l') {
        setShape('ellipse');
        setTool('ellipse');
      }
      if (e.key === '\\') {
        setShape('line');
        setTool('line');
      }
      // Illustrator puts the layers panel on F7 and keeps L for the ellipse.
      // Its default is Chrome's caret-browsing prompt on Windows and Linux
      // (QA 2026-09-24, layers-F19).
      if (e.key === 'F7') {
        e.preventDefault();
        toggleNavigation('layers');
      }
      if (key === 'c' && e.shiftKey) setTool('cloner');
      // C alone is the Comment tool: it brings the Comments tab out and drops pins (W5-AR2).
      if (key === 'c' && !e.shiftKey && !e.altKey && !viewingRef.current) {
        setCommenting((on) => !on);
        setCommentsOpen(true);
        setPanel(null);
      }
      if (key === 'v') setCommenting(false);
      // Shift-O is the artboard tool, where Illustrator keeps it.
      if (key === 'o' && e.shiftKey) setTool('artboard');
      // Four fingers on an iPad, and one key everywhere else.
      if (key === 'f' && !e.shiftKey) {
        setBare((on) => presenting || !on);
        setQuickAt(null);
      }
      /**
       * The view toggles, on the keys nearest Illustrator's. Illustrator spends command
       * on these, and command-R in a browser reloads the page — so they sit on bare keys
       * next to the punctuation Illustrator uses, and the panel says which is which.
       */
      if (e.key === "'") toggleView('grid');
      if (e.key === '"') toggleView('snapGrid');
      if (e.key === ';') toggleView('guides');
      if (e.key === ':') toggleView('lockGuides');
      if (key === 'u') toggleView('smart');
      // I takes a colour off the screen for what is chosen, as in Figma; shift-I for its outline.
      if (key === 'i' && !e.altKey && !viewingRef.current && selectionRef.current.length > 0) {
        e.preventDefault();
        eyedropSelection(e.shiftKey ? 'stroke' : undefined);
      }
      // Alt-R for the rulers: both the physical key and the letter, because alt-R on a
      // Mac types '®' and only `code` still says R, while on Windows `key` is a plain 'r'.
      if (e.altKey && (e.code === 'KeyR' || key === 'r')) {
        e.preventDefault();
        toggleView('rulers');
      }
      // Unless something on the canvas already took this Escape — a stroke put back, a pick
      // dropped. Read once the key has been everywhere, since which listener runs first
      // is not ours to count on (QA 2026-09-25, pencil-fill-brush-F12).
      if (e.key === 'Escape') window.setTimeout(() => !e.defaultPrevented && setNavigation((open) => (open === 'layers' ? null : open)));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flags, shape, toggleView, togglePanel, toggleNavigation, eyedropSelection]);

  /**
   * What the tool in hand will draw with. These controls belong to the tool, so they set
   * what it draws NEXT and never reach back to whatever is still selected — repainting
   * something already on the page is the selection's section of the panel.
   */
  const drawWith = useCallback((patch: Partial<EditorStyle>) => {
    setStyle((s) => ({ ...s, ...settled(patch, s) }));
  }, []);

  /** The same choice, made about the selection. */
  const restyle = useCallback(
    (patch: Partial<EditorStyle>) => {
      const full = settled(patch, styleRef.current);
      // With layers chosen, only they change. Their opacity used to become the drawing
      // style too, so everything drawn next was see-through (QA 2026-09-24, shapes-F3).
      if (selection.length === 0) setStyle((s) => ({ ...s, ...full }));
      canvas.restyle(full);
    },
    [canvas, selection.length],
  );

  /**
   * Repeat from the Edit dock (`shell/RepeatPanel.tsx`, `tools/repeat.ts`). A variation's
   * every setting is one press away: it is chosen and the Cloner tool's strip opens on it.
   */
  const repeatActions: RepeatActions = {
    repeat: () => {
      canvas.repeat();
    },
    set: (id, props) => canvas.update(id, props),
    vary: (clonerId, recipe) => canvas.addVariation(clonerId, recipe),
    unvary: (effectorId) => canvas.removeVariation(effectorId),
    openVariation: (effectorId) => {
      canvas.select([effectorId]);
      setTool('cloner');
    },
    makeLayers: (clonerId) => canvas.makeLayers(clonerId),
    stop: (clonerId) => canvas.stopRepeating(clonerId),
    node: (id) => session.open?.scene.get(id),
    source: (clonerId) => canvas.repeatSource(clonerId),
    paths: (clonerId) => canvas.repeatPaths(clonerId),
  };

  /**
   * What Assets does to the page. Read at the press, never held: the pictures a file holds
   * change as it is edited, and the panel asks again each time it draws (`sceneVersion`
   * re-renders it).
   */
  // Anything Assets puts on the page, pressed or dragged (`aimed` comes through here too): one
  // signal for the tours' "put one on the page" step, whichever kind it was (lib/tours).
  const placed = (kind: string) => emitHintSignal('asset.placed', { kind });
  const libraryActions: LibraryActions = {
    projectId: projectRef.current,
    useElement: ref => { setGenerationReferenceRequest({ key: crypto.randomUUID(), elements: [ref] }); openLibraryAt.current('generate'); },
    addShape: (shape, at) => { canvas.addShape(shape, at); placed('shape'); },
    addText: (preset, at) => { canvas.addText(preset, at); placed('text'); },
    addPicture: (picture, at) => { canvas.addPicture(picture, at); placed('picture'); },
    upload: () => pickPicture.current?.click(),
    filePictures: () => canvas.filePictures(),
    currentArtboard: () => canvas.exportBoards('board')[0] ?? null,
    // The page in a few plain lines, for the Templates panel's category (Jev; pageSummary.ts).
    pageSummary: () => {
      const board = canvas.exportBoards('board')[0];
      return session.open && board ? pageSummary(session.open.scene, board.id, { fileName: session.name }) : null;
    },
    // What is chosen is painted; with nothing to paint, what is drawn next is.
    useColour: (paint) => {
      // A gradient is a paint for what is there; the tools draw in one colour.
      if (!canvas.paintSelection(paint) && typeof paint === 'string') drawWith({ fill: paint, fillOn: true });
    },
    placeDrawing: (svg, options) => { canvas.placeDrawing(svg, options); placed('graphic'); },
    placeLayers: (layers, size, at) => { canvas.placeLayers(layers, size, at); placed('graphic'); },
    placeTemplate: (template, at) => { canvas.placeTemplate(template, at); placed('template'); },
    clipSelection: () => canvas.clipSelection(),
    notice: (message) => setToast(message),
  };

  const saveArtboardTemplate = (id: string, returnFocus: HTMLElement | null = document.activeElement as HTMLElement | null) => {
    const template = canvas.clipArtboard(id);
    if (template) setTemplateDraft({ template, returnFocus });
    else setToast('This artboard is no longer available.');
  };

  const toggleNode = (id: string, field: 'visible' | 'locked') => {
    const open = session.open;
    const node = open?.scene.get(id);
    if (!open || !node) return;
    applyOps(open.page, [{ op: 'set', id, props: { [field]: !node[field] } }], { scene: open.scene, origin: LOCAL_ORIGIN });
  };

  /** What a layer row offers when it is swiped aside, in Procreate's order. */
  const layerActions: LayerAction[] = [
    { id: 'lock', label: 'Lock', icon: 'lock', run: (id) => toggleNode(id, 'locked') },
    {
      id: 'duplicate',
      label: 'Duplicate',
      icon: 'copy',
      run: (id) => {
        canvas.select([id]);
        canvas.duplicate();
      },
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'trash',
      danger: true,
      run: (id) => {
        canvas.select([id]);
        canvas.deleteSelection();
      },
    },
  ];

  /**
   * Which artboards an export covers. Unset, it follows the selection: several boards
   * chosen means those, otherwise the one in hand. Picking in the menu fixes it until the
   * selection changes.
   */
  const [pickedScope, setPickedScope] = useState<ExportScope | null>(null);
  const selectionKey = selection.join();
  useEffect(() => setPickedScope(null), [selectionKey]);
  const chosenBoards = canvas.exportBoards('selected');
  const allBoards = canvas.exportBoards('all');
  const hereBoard = canvas.exportBoards('board')[0] ?? null;
  /** The layers chosen, which can be exported alone. Null when only artboards are, or nothing. */
  const chosenLayers = canvas.exportLayers();
  // Exporting the artboard stays what Export does unless the selection is asked for: a
  // layer is left chosen after every edit, and a page exported as one word would be a surprise.
  const scope: ExportScope = pickedScope === 'layers' && !chosenLayers ? 'board' : (pickedScope ?? (chosenBoards.length > 1 ? 'selected' : 'board'));
  const scoped = scope === 'all' ? allBoards : scope === 'selected' ? chosenBoards : hereBoard ? [hereBoard] : [];

  // Whatever SVG has to show as a picture is drawn at the document's own resolution, and
  // never softer than twice the page: an SVG is often printed from.
  const svgPictureScale = Math.max(2, session.resolution / 72);
  /** What an SVG had to trade, said once after the download rather than never. */
  const tellTrades = (notes: string[]) => {
    const unique = [...new Set(notes)];
    if (unique.length > 0) setToast(`Downloaded. ${unique.join(' ')}`);
  };

  /** The layers chosen as one file of their own: drawn here, on nothing, in a box that fits them. */
  /** What a size adds to a file's name: `@2x`, or the resolution it was made at. */
  const sizeTag = (size: ExportSize) => (size.ppi ? `@${size.ppi}ppi` : size.scale > 1 ? `@${size.scale}x` : '');

  const exportLayersAs = (what: ExportCommand) => {
    if (!chosenLayers) return;
    // One layer is named after itself; several after the artboard they came from.
    const base = fileNames([chosenLayers.count === 1 ? chosenLayers.name : `${canvas.exportName()}-selection`], 'x')[0]!.replace(/\.x$/, '');
    if (what === 'svg') {
      const made = canvas.exportLayersSvg(svgPictureScale);
      if (made) download(new Blob([made.svg], { type: 'image/svg+xml' }), `${base}.svg`);
      tellTrades(made?.notes ?? []);
      return;
    }

  };

  /** One PNG of what a browser export shipped, for Taste: a board drawn at most TASTE_PICTURE_PX on its long side. */
  const tastePicture = (board?: { id: string; width: number; height: number }) =>
    board ? canvas.exportPng(Math.min(1, TASTE_PICTURE_PX / Math.max(board.width, board.height, 1)), board.id) : null;

  const exportOtherAs = (what: ExportCommand, size: ExportSize) => {
    if (scope === 'layers') return exportLayersAs(what);
    const name = canvas.exportName();
    const boards = scoped;
    const docName = session.name || 'Artwork';
    const zipName = `${fileNames([docName], 'zip')[0]}`;
    if ((what === 'mp4' || what === 'gif') && boards.length > 0 && session.open?.pageId) {
      // A video is drawn on this machine, in an export page of its own that keeps going behind
      // other tabs (Tarek, 3 Nov: "keep renders on the machine itself if I'm manually exporting").
      // The render service is still where it goes when it has a clip or a sketch, when the page
      // could not be opened, and when the page finds this browser cannot encode it.
      const pageId = session.open.pageId;
      const targets = boards.map((board) => ({ pageId, artboardId: board.id }));
      const names = fileNames(boards.map((board) => board.name), what);
      const made = canvas.exportVideo({ artboardIds: boards.map((board) => board.id), names, scale: size.scale, fps: what === 'mp4' ? 30 : 15, format: what });
      if ('cloud' in made) {
        void cloud.start(what, targets, zipName, size.scale);
        return;
      }
      const opened = openExportPage(documentId);
      if (!opened) {
        setToast('Your browser blocked the export page, so this video is rendering in the cloud.');
        void cloud.start(what, targets, zipName, size.scale);
        return;
      }
      const first = boards[0]!;
      const cover = canvas.exportPng(Math.min(1, 480 / Math.max(first.width, first.height, 1)), first.id) ?? undefined;
      handOver(opened, prepareVideoSnapshot(made.snapshot).then((snapshot) => ({
        snapshot,
        documentName: docName,
        fileName: boards.length === 1 ? names[0]! : zipName,
        ...(cover ? { cover } : {}),
        cloud: { pageId, targets, zipName, scale: size.scale },
      })));
      setToast('Rendering in a new tab on this computer.');
    } else if (what === 'pdf' || what === 'mp4' || what === 'gif') {
      const pageId = session.open?.pageId;
      const targets = pageId ? boards.map((board) => ({ pageId, artboardId: board.id })) : [];
      void cloud.start(what, targets.length ? targets : null, zipName, what === 'pdf' ? undefined : size.scale);
    } else if (what === 'slices') {
      // One file per slice, each named after it — a handful of files is what slices are for.
      // Named as board exports are, made unique: two slices of the same name downloaded
      // two files of the same name (QA 2026-09-24, cut-slice-measure-F18).
      const cut = canvas.exportSlices(svgPictureScale);
      const names = fileNames(cut.map((slice) => slice.name || 'slice'), 'svg');
      cut.forEach((slice, index) => download(new Blob([slice.svg], { type: 'image/svg+xml' }), names[index]!));
      tellTrades(cut.flatMap((slice) => slice.notes));
    } else if (what === 'svg') {
      if (boards.length > 1) {
        const names = fileNames(boards.map((board) => board.name), 'svg');
        const written = boards.map((board) => canvas.exportSvg(board.id, svgPictureScale));
        const files = written.flatMap((made, index) => (made ? [{ name: names[index]!, bytes: new TextEncoder().encode(made.svg) }] : []));
        download(new Blob([zip(files) as BlobPart], { type: 'application/zip' }), zipName);
        tellTrades(written.flatMap((made) => made?.notes ?? []));
        if (files.length) void keepExportForTaste(documentId, 'svg', tastePicture(boards[0]), files.length);
        return;
      }
      const made = canvas.exportSvg(boards[0]?.id, svgPictureScale);
      if (made) download(new Blob([made.svg], { type: 'image/svg+xml' }), `${name}.svg`);
      tellTrades(made?.notes ?? []);
      if (made) void keepExportForTaste(documentId, 'svg', tastePicture(boards[0]), 1);
    }
  };

  const [localExport, setLocalExport] = useState<string | null>(null);
  const [exportFailure, setExportFailure] = useState<string | null>(null);
  const exportController = useRef<AbortController | null>(null);
  useEffect(() => () => exportController.current?.abort(), [linkedId]);

  const exportAs = async (what: ExportCommand, size: ExportSize): Promise<boolean> => {
    if (exportController.current || cloud.state?.status === 'QUEUED' || cloud.state?.status === 'RENDERING') return false;
    // A PNG and a JPEG are drawn here in the page, artboards and layers alike; only a PDF goes to the render service.
    if (what !== 'png' && what !== 'jpg') {
      exportOtherAs(what, size);
      return true;
    }
    const controller = new AbortController();
    exportController.current = controller; // Guard before React renders, including repeated clicks/shortcuts.
    setExportFailure(null);
    setToast(null);
    setLocalExport('Preparing export…');
    try {
      const layers = scope === 'layers';
      if (layers && !chosenLayers) throw new Error('Choose a layer to export.');
      const base = layers ? (chosenLayers!.count === 1 ? chosenLayers!.name : `${canvas.exportName()}-selection`) : canvas.exportName();
      const names = fileNames(layers ? [`${base}${sizeTag(size)}`] : scoped.map(board => `${board.name}${sizeTag(size)}`), what);
      const targets = layers ? [{ name: names[0]! }] : scoped.map((board, index) => ({ artboardId: board.id, name: names[index]! }));
      const bytes = await canvas.exportRaster({ scale: size.scale, format: what, selection: layers, targets, signal: controller.signal, onProgress: setLocalExport });
      controller.signal.throwIfAborted();
      const bundled = targets.length > 1;
      const filename = bundled ? fileNames([session.name || 'Artwork'], 'zip')[0]! : names[0]!;
      download(new Blob([bytes as BlobPart], { type: bundled ? 'application/zip' : what === 'jpg' ? 'image/jpeg' : 'image/png' }), filename);
      setToast(`${what.toUpperCase()} export ready. Download started.`);
      if (what === 'png') {
        // The PNG itself when it is one file of a sensible size; else the first board drawn smaller.
        const own = !bundled && bytes.byteLength <= TASTE_REUSE_BYTES ? bytes : null;
        void keepExportForTaste(documentId, 'png', own ?? (layers ? null : tastePicture(scoped[0])), targets.length);
      }
      return true;
    } catch (error) {
      const cancelled = controller.signal.aborted || (error instanceof Error && error.name === 'AbortError');
      const message = cancelled ? 'Export cancelled.' : error instanceof Error ? error.message : 'Export failed. Please try again.';
      if (!cancelled) setExportFailure(message);
      setToast(message);
      return false;
    } finally {
      if (exportController.current === controller) {
        exportController.current = null;
        setLocalExport(null);
      }
    }
  };

  // ── the menus ─────────────────────────────────────────────────────────────

  const exportOptions: ExportOptions = {
    // The format this page is most likely wanted in (Jev, from the page in a few lines): preselected while nobody has picked one.
    suggest: async () => {
      const board = scoped[0];
      if (!session.open || !board || scope === 'layers') return null;
      const moves = !!canvas.recipeOf(board.id) || !!canvas.timelineOf(board.id)?.children.some((layer) => layer.animation);
      const answer = await askPage('export', documentId, pageSummary(session.open.scene, board.id, { fileName: session.name, moves }));
      return worthIt(answer) ? (answer!.value as 'png' | 'jpg' | 'svg' | 'pdf' | 'mp4' | 'gif') : null;
    },
    scope, count: scope === 'layers' ? 1 : scoped.length, resolution: session.resolution, slices: canvas.sliceCount(),
    size: scope === 'layers' ? chosenLayers?.size ?? null : scoped[0] ? { width: scoped[0].width, height: scoped[0].height } : null,
    sizes: scope === 'layers' ? chosenLayers ? [chosenLayers.size] : [] : scoped,
    activity: localExport, error: exportFailure,
    onScopeChange: setPickedScope, onExport: exportAs, onCancel: () => exportController.current ? exportController.current.abort() : void cloud.cancel(),
    again: cloud.ready ? { name: CLOUD_NAMES[cloud.ready.format], busy: cloud.fetchingAgain, run: () => void cloud.again().catch((error: Error) => setToast(error.message)) } : null,
    // Where it can go is the kit's Send to list for the export's kind (the Upscaler takes a JPEG or MP4, never a PDF…).
    send: cloud.ready ? { apps: exportTargets(cloud.ready.format), busy: sendingExport, logo: appLogo, run: (app: SendApp) => { const ready = cloud.ready; if (!ready || sendingExport) return; setSendingExport(app); void sendExport(app, ready.jobId, session.name || 'Artwork export').catch((error: Error) => setToast(error.message)).finally(() => setSendingExport(null)); } } : null,
    // One artboard: on a Flow board as layers, with no export (the same send as the artboard's Open with › Flow).
    flow: session.open && scope !== 'layers' && scoped.length === 1 ? { label: 'Flow', logo: appLogo('flow'), run: () => {
      const open = session.open;
      if (!open) return;
      void sendArtboardToFlow(flowSourceOf(open), scoped[0]!.id).then(({ leftOut }) => { if (leftOut > 0) setToast(leftOutWords(leftOut)); }).catch((error: Error) => setToast(error.message || 'It could not be sent to Flow. Try again.'));
    } } : null,
    scopes: [
      // The layers chosen come first when there are any: they are what the hand is on.
      ...(chosenLayers ? [{ id: 'layers' as const, label: 'Selection', detail: chosenLayers.count === 1 ? `${chosenLayers.name}, on its own` : `${chosenLayers.name}, together as one file` }] : []),
      { id: 'board', label: 'Artboard', detail: hereBoard?.name || 'Current artwork' },
      ...(chosenBoards.length > 1 ? [{ id: 'selected' as const, label: 'Selected', detail: `${chosenBoards.length} selected artboards` }] : []),
      ...(allBoards.length > 1 ? [{ id: 'all' as const, label: 'All', detail: `${allBoards.length} artboards` }] : []),
    ],
  };

  const viewItems: MenuEntry[] = [
    ...([
      ['rulers', 'Rulers', '⌥R'],
      ['grid', 'Grid', "'"],
      ['transparency', 'Transparency Grid', ''],
    ] as const).map(([key, label, keys]) => ({ id: key, label, shortcut: keys || undefined, checked: view[key as ViewKey], onSelect: () => toggleView(key as ViewKey) })),
    { kind: 'separator' },
    ...([
      ['guides', 'Guides', ';'],
      ['lockGuides', 'Lock Guides', ':'],
      ['smart', 'Smart Guides', 'U'],
    ] as const).map(([key, label, keys]) => ({ id: key, label, shortcut: keys, checked: view[key as ViewKey], onSelect: () => toggleView(key as ViewKey) })),
    { kind: 'separator' },
    ...([
      ['snapPoint', 'Snap to Point', ''],
      ['snapGrid', 'Snap to Grid', '"'],
      ['snapPixel', 'Snap to Pixel', ''],
    ] as const).map(([key, label, keys]) => ({ id: key, label, shortcut: keys || undefined, checked: view[key as ViewKey], onSelect: () => toggleView(key as ViewKey) })),
    { kind: 'separator' },
    { id: 'clear-guides', label: 'Clear Guides', disabled: canvas.guideCount() === 0, onSelect: () => canvas.clearGuides() },
  ];

  /**
   * Edit ▸ Select, as a Mac app has it: everything, nothing, and — while the arrow is in
   * hand — its ways of growing a choice, which are too useful to lose and too rare to be
   * a row of marks on screen all day.
   */
  const reaching: readonly RailButton[] = tool === 'select' && rail?.bottom?.kind === 'buttons' ? rail.bottom.items : [];
  const selectItems: MenuEntry[] = [
    { id: 'all', label: 'Select all', icon: 'select-area', shortcut: '⌘A', onSelect: () => canvas.selectAll() },
    { id: 'none', label: 'Deselect', icon: 'close', disabled: selection.length === 0, onSelect: () => canvas.select([]) },
    ...(reaching.length ? [{ kind: 'separator' as const }] : []),
    ...reaching.flatMap((item): MenuEntry[] => [
      { id: item.id, label: REACH_NAMES[item.id] ?? item.title, icon: item.icon, checked: item.pressed, disabled: item.disabled, onSelect: () => item.run({ more: false }) },
      ...(item.id === 'same' ? [{ id: 'same-size', label: 'Select same size', icon: 'dimension' as const, disabled: item.disabled, onSelect: () => item.run({ more: true }) }] : []),
    ]),
  ];

  // The page being worked on moves with a press inside a page, which can choose nothing
  // before or after it and so re-render nothing here — and the Layers panel shows that page
  // (Tarek, 2026-10-01: "when I press on an artboard the side layer panel doesn't update").
  // Asked on every view change, which is how the canvas says a page was pressed; a page that
  // stays the same re-renders nothing.
  const [, setWorkingBoard] = useState<string | null>(null);
  useEffect(() => canvas.onViewChange(() => setWorkingBoard(canvas.exportArtboard())), [canvas]);
  const boards = canvas.artboards();
  // The file's Share popup: its project's people, and its view link. ⇧⌘S, Document ▸ Share…, and the toolbar.
  const share = () => openFileShare({ id: documentId, name: session.name || 'Untitled' });
  useShareShortcut(share, Boolean(documentId));
  /** Share from a button or a menu, not the key: the hints teach ⇧⌘S (lib/hints). */
  const shareByButton = () => { emitHintSignal('share.opened', { viaButton: true }); share(); };
  // An access-request notification lands here with ?share=requests: once the file is open,
  // Share opens on who asked (the popup shows the requests only to people who can share it).
  const fileOpen = Boolean(session.open) && !session.stopped;
  useEffect(() => {
    if (!fileOpen) return;
    const section = takeShareSectionFromUrl();
    if (section) openFileShare({ id: documentId, name: session.name || knownName || 'Untitled' }, { section });
    // Once per open file, and again when the bell leads here (a new location key); the
    // parameter is gone from the address after the first read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileOpen, documentId, location.key]);
  // A file the Home has just made starts with what this person likes new files to start with (Settings ›
  // Artwork: units, colour, resolution). Written once: a reload keeps the route state, and must not undo what
  // they have changed since.
  const startWith = (location.state as { startWith?: Partial<NewFileDefaults> } | null)?.startWith;
  useEffect(() => {
    if (!fileOpen || !startWith || !session.open) return;
    const key = `artwork:started:${documentId.toLowerCase()}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      // No storage: the route state alone decides, which is right for the first open.
    }
    writeMeta(session.open.root, { units: startWith.units, colorMode: startWith.colorMode, resolution: startWith.resolution }, LOCAL_ORIGIN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileOpen, documentId]);
  /**
   * Edit ▸ Cut, Copy, Paste, Paste here, Copy as (W5-AR3). Paste reads the system clipboard
   * when the browser lets it, so shapes copied in another file arrive too; refused, it pastes
   * what was copied in this tab.
   */
  const pasteFromClipboard = async (at?: { x: number; y: number }) => {
    let text = '';
    // Reading the clipboard asks the person first unless they already allowed it. With a copy
    // made in this tab there is nothing to ask for: that copy is pasted at once.
    const allowed = await navigator.permissions
      ?.query({ name: 'clipboard-read' as PermissionName })
      .then((status) => status.state === 'granted')
      .catch(() => false);
    if (allowed || !canvas.hasCopy()) {
      try {
        text = (await navigator.clipboard?.readText?.()) ?? '';
      } catch {
        // Not allowed to read it: what this tab copied is pasted instead.
      }
    }
    if (!canvas.pasteText(text, at)) setToast('Nothing to paste. Copy something first.');
  };
  /** The chosen layers, or the one artboard chosen, as a picture on the clipboard. */
  const copyAs = async (format: 'png' | 'svg') => {
    const board = !chosenLayers && chosenBoards.length === 1 ? chosenBoards[0]!.id : undefined;
    try {
      if (format === 'png') {
        // Every pixel of each picture, not the copy sized for the screen.
        await canvas.loadOriginals(board);
        const bytes = board ? canvas.exportPng(2, board) : canvas.exportLayersPng(2);
        if (!bytes) throw new Error('nothing drawn');
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': new Blob([bytes as BlobPart], { type: 'image/png' }) })]);
      } else {
        const made = board ? canvas.exportSvg(board, svgPictureScale) : canvas.exportLayersSvg(svgPictureScale);
        if (!made) throw new Error('nothing drawn');
        await navigator.clipboard.writeText(made.svg);
      }
      setToast(format === 'png' ? 'Copied as PNG.' : 'Copied as SVG.');
    } catch {
      setToast('That could not be copied. Try Export instead.');
    }
  };
  const clipboard: Omit<ClipboardOptions, 'chosen' | 'editable' | 'here'> = {
    drawable: Boolean(chosenLayers) || chosenBoards.length === 1,
    readOnly: viewing,
    cut: () => canvas.cutSelection(),
    copy: () => void canvas.copySelection(),
    paste: (at) => void pasteFromClipboard(at),
    copyAs: (format) => void copyAs(format),
    copyProperties: () => void canvas.copyProperties(),
    pasteProperties: () => void canvas.pasteProperties(),
    hasProperties: canvas.hasProperties(),
  };
  /** Says something in the toast, with one action beside it (Undo). */
  const sayWith = (text: string, action: { label: string; run: () => void }) => {
    setToastText(text);
    setNoticeAction(action);
  };
  const restoreTo = (version: FileVersion) => {
    if (restoring) return;
    setRestoring(true);
    restoreVersion(documentId, version.id)
      .then((answer) => {
        setChosenVersion(null);
        setNavigation(null);
        void history.refresh();
        sayWith('Restored for everyone in this file.', {
          label: 'Undo',
          run: () => {
            restoreVersion(documentId, answer.undo)
              .then(() => {
                setToast('Put back as it was.');
                void history.refresh();
              })
              .catch(() => setToast('That could not be undone.'));
          },
        });
      })
      .catch((error: Error) => setToast(error.message || 'That version could not be restored.'))
      .finally(() => setRestoring(false));
  };
  const copyFrom = (version: FileVersion) => {
    setToast('Making a copy…');
    copyVersion(documentId, version.id)
      .then((copy) => {
        setToast(null);
        navigate(`/f/${copy.id}`, { state: { name: copy.name } });
      })
      .catch((error: Error) => setToast(error.message || 'That version could not be copied.'));
  };
  const saveName = (name: string) => {
    const was = naming?.version ?? null;
    setNaming(null);
    (was ? renameVersion(documentId, was.id, name) : saveVersion(documentId, name))
      .then((version) => {
        history.put(version);
        if (chosenVersion?.id === version.id) setChosenVersion(version);
        if (!was) setToast(`Saved as “${version.name}”.`);
      })
      .catch((error: Error) => setToast(error.message || 'That name could not be saved.'));
  };
  /* ── comments (W5-AR2) ─────────────────────────────────────────────── */
  const pinsShown = !viewing && !bare && (commentsOpen || commenting);
  // The click layer and the pins over the canvas hand a pinch to it, never to the browser.
  const commentLayer = useWheelToCanvas<HTMLDivElement>();
  const pinLayer = useWheelToCanvas<HTMLDivElement>();
  const comments = useComments(documentId, { me: { id: 'me', name: 'You', avatarUrl: null }, active: pinsShown, onError: (message) => setToast(message) });
  // The file's people, for @: its project's, as the Share popup lists them. Read once a visit.
  const peopleAsked = useRef(false);
  useEffect(() => {
    if (!commentsOpen || peopleAsked.current || !documentId) return;
    peopleAsked.current = true;
    gatewayCall<{ people?: { kind?: string; id?: string; name?: string; avatarUrl?: string | null }[] }>('artwork', 'artwork.share.get', { type: 'file', id: documentId })
      .then((state) => setCommentPeople((state?.people ?? []).flatMap((one) => (one.kind === 'user' && one.id ? [{ id: one.id, name: one.name ?? null, avatarUrl: one.avatarUrl ?? null }] : []))))
      .catch(() => undefined);
  }, [commentsOpen, documentId]);
  // A chat's layered file lands here with ?layers=<its address> (layers/LayersImport, 29 Oct): placed once, on a new artboard
  // the picture's size, and the address taken off so a reload does not place it again.
  useEffect(() => {
    if (!fileOpen) return;
    const params = new URLSearchParams(window.location.search);
    const address = params.get('layers');
    if (!address) return;
    params.delete('layers');
    const rest = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
    // Not cancelled with the effect: the address is already taken off, so this is the one placing.
    fetchLayerFile(address)
      .then((file) => placeLayerFile(canvas, file, documentId).then(({ swapped, placed, total }) => setToast([placedWords(file, placed, total), fontsLine(swapped)].filter(Boolean).join(' '))))
      .catch((error: Error) => setToast(error.message || 'The layers could not be placed.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileOpen, documentId]);
  // A font sent from Flobi Fonts lands here with ?fonts=<Fonts id~format,…>&family=<name> (layers/FontReceive, 29 Oct):
  // written as the font picker writes a library family (every face under the family's own name), and the words taken
  // off so a reload adds nothing.
  useEffect(() => {
    if (!fileOpen) return;
    const params = new URLSearchParams(window.location.search);
    const faces = facesFromParam(params.get('fonts'));
    const family = (params.get('family') ?? '').trim();
    if (!params.has('fonts')) return;
    params.delete('fonts');
    params.delete('family');
    const rest = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
    if (!faces.length) return;
    resolveLibraryFamily(family || 'Font', libraryFaces(faces, family || 'Font'))
      .then((font) => {
        canvas.addFont(font);
        setToast(`"${font.family}" is in this file's fonts. Choose it in Font on any text.`);
      })
      .catch((error: Error) => setToast(error.message || 'The font could not be added.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileOpen, documentId]);
  // A comment notification lands here with ?comment=<thread>: the tab opens on that thread.
  useEffect(() => {
    if (!fileOpen) return;
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get('comment');
    if (!wanted) return;
    params.delete('comment');
    const rest = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
    setCommentsOpen(true);
    setPanel(null);
    setChosenThread(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileOpen, documentId, location.key]);
  // Asking for a panel, Comments or Export in full screen comes back to the chrome, the only place they can show.
  const askedFor = useRef({ panel, navigation, commentsOpen });
  useEffect(() => {
    const was = askedFor.current;
    askedFor.current = { panel, navigation, commentsOpen };
    if (!presenting && ((panel && panel !== was.panel) || (navigation && navigation !== was.navigation) || (commentsOpen && !was.commentsOpen))) setBare(false);
  }, [panel, navigation, commentsOpen, presenting]);
  useEffect(() => {
    if (presenting) return;
    const leave = () => setBare(false);
    window.addEventListener('artwork:export', leave);
    return () => window.removeEventListener('artwork:export', leave);
  }, [presenting]);
  // Escape puts the Comment tool down and drops a pin still waiting for words. Heard first (capture),
  // since the canvas takes Escape for its own picks before the page's handler hears it.
  useEffect(() => {
    if (!commenting && !draftPin) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || menuIsOpen() || keyBelongsToField(event.target, event.key)) return;
      setCommenting(false);
      setDraftPin(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [commenting, draftPin]);
  // Pins follow the view: drawn again when it moves, once a frame at most.
  const [, setPinFrame] = useState(0);
  useEffect(() => {
    if (!pinsShown) return;
    let frame = 0;
    const stop = canvas.onViewChange(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setPinFrame((n) => n + 1);
      });
    });
    return () => {
      stop();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [canvas, pinsShown]);
  const pageId = session.open?.pageId ?? null;
  const worldBox = (id: string) => canvas.worldBoxOf(id);
  const openThreads = (comments.threads ?? []).filter((one) => !one.resolved);
  const pinNumbers = new Map(openThreads.map((one, index) => [one.id, index + 1] as const));
  const dropPin = (at: { x: number; y: number }) => {
    if (!pageId || !session.open) return;
    const scene = session.open.scene;
    const world = canvas.worldAt(at);
    const layers = canvas.layersAt(at).map((id) => ({ id, artboard: scene.get(id)?.type === 'artboard' }));
    const boards = canvas.artboards().map((board) => board.id);
    setDraftPin(anchorAt({ pageId, world, layers, boxOf: worldBox, boards }));
    setChosenThread(null);
    setCommentsOpen(true);
  };
  const postDraft = (body: string, mentions: string[]) => {
    const anchor = draftPin;
    if (!anchor) return;
    setDraftPin(null);
    void comments.create(anchor, body, mentions).then((made) => {
      if (made) setChosenThread(made.id);
    });
  };
  const zoomMenu: MenuEntry[] = [
    { id: 'in', label: 'Zoom In', icon: 'zoom-in', onSelect: () => canvas.zoomBy(1.25) },
    { id: 'out', label: 'Zoom Out', icon: 'zoom-out', onSelect: () => canvas.zoomBy(1 / 1.25) },
    { kind: 'separator' },
    { id: 'fit', label: 'Zoom to Fit', icon: 'fit', shortcut: '0', onSelect: () => { canvas.fit(); emitHintSignal('view.zoomed', { viaMenu: true }); } },
    { id: 'actual', label: 'Actual Size', icon: 'actual-size', shortcut: '1', onSelect: () => { canvas.actualSize(); emitHintSignal('view.zoomed', { viaMenu: true }); } },
    { kind: 'separator' },
    ...[0.5, 1, 2, 4].map((level) => ({
      id: `z${level}`,
      label: `${level * 100}%`,
      checked: Math.abs(zoom - level) < 0.005,
      onSelect: () => canvas.zoomBy(level / zoom),
    })),
  ];

  const documentMenu: MenuEntry[] = [
    // A viewer sees the file's name and looks at it; renaming, copying, settings, placing and deleting are not theirs.
    ...(viewing ? [] : [
      { id: 'rename', label: 'Rename…', onSelect: () => window.dispatchEvent(new Event('artwork:rename')) },
      // A copy of the whole file, opened once it is made (ART-UX-06).
      {
        id: 'duplicate', label: 'Duplicate', onSelect: () => {
          // The pictures still going up are not in the file yet, so a copy now would miss them (review E8).
          if (uploading > 0) {
            setToast('Wait for the pictures to finish uploading, then duplicate the file.');
            return;
          }
          setToast('Making a copy…');
          duplicateFile(documentId)
            .then((copy) => {
              setToast(null);
              navigate(`/f/${copy.id}`, { state: { name: copy.name } });
            })
            .catch((error: Error) => setToast(error.message || 'The file could not be copied.'));
        },
      },
      versionHistoryMenu({
        versions: history.versions,
        open: (version) => {
          setNavigation('versions');
          setChosenVersion(version);
          void history.refresh();
        },
        nameCurrent: () => setNaming({ version: null }),
      }),
      { id: 'settings', label: 'Document settings…', onSelect: () => setSettingsOpen(true) },
      // On a phone the top bar has no room for these two, so the title menu carries them.
      ...(phone
        ? [
            { id: 'layers', label: layersOpen ? 'Hide Layers' : 'Layers', onSelect: () => toggleNavigation('layers') },
            { id: 'animate', label: animating ? 'Stop Animating' : 'Animate', onSelect: () => togglePanel('animate') },
            // And the top bar's Properties and Comment, which a phone's bar has no room for.
            { id: 'properties', label: panel === 'format' ? 'Hide Properties' : 'Properties', shortcut: '⌥⌘I', onSelect: () => togglePanel('format') },
            { id: 'comments', label: commenting ? 'Stop Commenting' : 'Comment', shortcut: 'C', onSelect: () => { setCommenting((on) => !on); setCommentsOpen(true); setPanel(null); } },
            ...(commentsOpen && !commenting ? [{ id: 'comments-hide', label: 'Hide Comments', onSelect: () => setCommentsOpen(false) }] : []),
          ]
        : []),
    ]),
    // Share stays for a viewer too: the popup opens read-only for someone who can't change who has access.
    { id: 'share', label: 'Share…', shortcut: '⇧⌘S', onSelect: shareByButton },
    // The whole file, the media inside it. Open artboard with, on the canvas, sends the one artboard.
    ...wholeOpenWith({ documentId, name: session.name, open: session.open, onError: setToast }, 'Open file with'),
    { kind: 'separator' },
    ...(viewing ? [] : [
      {
        kind: 'submenu' as const, id: 'edit', label: 'Edit', items: [
          ...clipboardItems({ ...clipboard, chosen: selection.length > 0, editable: selection.some((id) => !session.open?.scene.get(id)?.locked) }),
          { kind: 'separator' as const },
          { id: 'insert', label: 'Place image…', onSelect: () => pickPicture.current?.click() },
          { kind: 'separator' as const },
          ...selectItems,
        ],
      },
    ]),
    {
      kind: 'submenu', id: 'view', label: 'View', items: [
        ...viewItems,
        { kind: 'separator' },
        ...zoomMenu.slice(0, 5),
        { kind: 'separator' },
        ...(viewing || phone ? [] : [{ id: 'properties', label: 'Properties', shortcut: '⌥⌘I', checked: formatOut, onSelect: () => togglePanel('format') }]),
        { id: 'full', label: 'Enter full screen', shortcut: 'F', onSelect: () => setBare(true) },
        { id: 'shortcuts', label: 'Keyboard shortcuts', shortcut: '?', onSelect: () => setShortcutsOpen(true) },
      ],
    },
    // Appearance (light, dark, system, and Artwork's own) is the person's, on the Preferences page of the Settings window.
    { id: 'app-settings', label: 'Artwork settings…', onSelect: () => settingsWindow.open({ group: 'app' }) },
    // Learn Artwork: opens the lessons card in the corner (lib/tours), until every step is done.
    ...(getStarted && !viewing ? [{ id: 'get-started', label: `${getStarted.label} · ${getStarted.count}`, onSelect: getStarted.go }] : []),
    ...(viewing ? [] : [
      { kind: 'separator' as const },
      { id: 'delete-file', label: 'Move to Trash…', danger: true, onSelect: () => setDeleting(true) },
    ]),
  ];

  // ── the side panel ────────────────────────────────────────────────────────

  const scene = session.open?.scene ?? null;
  const nodes = useMemo(
    () => (scene ? selection.map((id) => scene.get(id)).filter((node): node is NodeRecord => !!node) : []),
    // The scene is mutable; the version is what says it changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scene, selection, sceneVersion],
  );
  // Selection colours are listed for what holds other layers — a group, an artboard, several
  // layers — never for one shape, whose Fill and Stroke already say it.
  const colorsChosen = useMemo(
    () => (panelOut && scene && (nodes.length > 1 || nodes.some((node) => COLOR_HOLDERS.has(node.type))) ? selectionColors(scene, selection) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [panelOut, scene, selection, sceneVersion, nodes],
  );
  // The agent panel shows what is chosen as chips and sends it with the next message;
  // nothing once the editor closes. Told again only when a layer's id, name or type changes.
  const agentSelection = JSON.stringify(nodes.map(layerItem));
  useEffect(() => setAgentSelection(JSON.parse(agentSelection)), [agentSelection]);
  useEffect(() => () => setAgentSelection([]), []);
  // Only a page plays, so Animate is offered only when everything chosen sits on one.
  const onArtboard = !!scene && nodes.length > 0 && nodes.every((node) => [...scene.ancestors(node.id)].some((id) => scene.get(id)?.type === 'artboard'));

  /** Asks for a picture to put in these image layers, in place of the one they show. */
  const replaceImage = (ids: readonly string[]) => {
    replacing.current = ids;
    pickReplacement.current?.click();
  };
  const arrangeAll = (order?: string[]) =>
    canvas.arrangeArtboards({ order, columns: grid.columns ?? columnsFor(canvas.exportBoards('all').length), gap: grid.gap });
  const actions: SelectionActions = {
    // Sized where they stand. Laying the grid out again afterwards is Tidy Up's job, done
    // when asked: re-arranging by itself after an edit is what Figma's tidy-up patents claim.
    sizeBoards: (ids, preset) => canvas.sizeArtboards(ids, preset as ArtboardPreset),
    tidyBoards: () => arrangeAll(),
    middleOf: (id) => canvas.middleOf(id),
    boxInParent: (id) => canvas.boxInParent(id),
    editGradient: (target) => canvas.editGradient(target),
    exportBoards: () => window.dispatchEvent(new Event('artwork:export')),
    update: (id, props) => canvas.update(id, props),
    updateAll: (ids, props) => canvas.updateAll(ids, props),
    restyle: (patch) => restyle(patch),
    align: (how) => canvas.align(how),
    tidyUp: (how) => canvas.tidyUp(how),
    combine: (op) => canvas.combine(op),
    canCombine: !!scene && flags.isEnabled('artwork.booleans') && canCombineSelection(scene, selection),
    canMask: !!scene && geometrySelection(scene, selection).length > 1,
    mask: () => canvas.mask(),
    maskOf: id => scene ? selectionMask(scene, id) : null,
    canReleaseMask: id => !!scene && !scene.lockedHere(id),
    group: () => canvas.group(),
    ungroup: () => canvas.ungroup(),
    expandBlend: () => canvas.expandBlend(),
    releaseBlend: () => canvas.releaseBlend(),
    rasterize: () => void canvas.rasterize(),
    outlineText: () => void canvas.outlineText(),
    arrange: (how) => canvas.arrange(how),
    flip: (axis) => canvas.flip(axis),
    addEffect: (type, look) => void canvas.addEffect(type, look),
    previewEffects: (id, types, longEdge, onTile, signal) => canvas.previewEffects(id, types, longEdge, onTile, signal),
    setEffect: (id, index, props) => canvas.setEffect(id, index, props),
    removeEffect: (id, index) => canvas.removeEffect(id, index),
    moveEffect: (id, index, to) => canvas.moveEffect(id, index, to),
    setFont: (id, family, font) => canvas.setFont(id, family, font),
    applyShader: (id) => canvas.applyShader(id),
    getShader: (id) => canvas.getShader(id),
    sketchOf: (id) => canvas.sketchOf(id),
    setSketch: (id, props) => canvas.setSketch(id, props),
    pickLayer: (candidates, then) => canvas.pickLayer(candidates, then),
    thumbnailOf: (id) => canvas.thumbnailOf(id),
    deleteSelection: () => canvas.deleteSelection(),
    placeAnchor: (at) => canvas.placeAnchor(at),
    replaceImage: (ids) => replaceImage(ids),
    selectionColors: colorsChosen ?? undefined,
    recolor: (places, color) => { if (scene) canvas.updateEach(recolorWrites(scene, places, color)); },
    fitImage: (ids, how) => canvas.fitPicture(ids, how),
  };

  const mark = markOf(tool, KINDS.map((kind) => kind.id));
  /** What the panel calls the tool in hand: the shape's own name for a shape. */
  const toolLabel = KINDS.find((one) => one.id === tool)?.title ?? (tool === 'addon' ? (addons.heldName ?? 'Add-on') : tool === 'width' ? 'Width' : (ALL_TOOLS.find((one) => one.id === tool)?.label ?? 'Tool'));

  /** The page Animate is on, read once per render for the bar and its timeline. */
  const animationTarget = animating ? canvas.animationTarget() : null;
  const pickedLayer = animationTarget?.layer?.id ?? null;
  /** The picked layer as its moves' tiles draw it: a picture's own pixels, type as two letters, anything else drawn small. */
  const layerPicture = useMemo((): LayerPicture => {
    const open = session.open;
    const node = pickedLayer && open ? open.scene.get(pickedLayer) : null;
    if (!open || !node) return { kind: 'shape' };
    if (node.type === 'text') return { kind: 'text' };
    try {
      // A picture's pixels come from where they live: an SVG shown as an image may not fetch them.
      const assetId = node.type === 'image' ? (node as { assetId?: string }).assetId : undefined;
      const src = assetId ? assetsMap(open.root).get(assetId)?.src : undefined;
      if (src) return { kind: 'picture', src };
      return { kind: 'picture', src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(toSvg(open.scene, { nodeId: node.id, padding: 1, indent: false }))}` };
    } catch {
      // A layer the writer cannot draw still has moves to choose: they are shown on a plain shape.
      return { kind: 'shape' };
    }
    // The scene is mutable; the version is what says it changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.open, pickedLayer, sceneVersion]);
  const animationStrip = animationTarget ? canvas.timelineOf(animationTarget.page.id) : null;
  // The recipe that suits a page (Jev, from the page in a few lines): Animate marks its card Suggested.
  const suggestRecipe = useCallback(async (pageId: string) => {
    if (!session.open) return null;
    const recipes = MAGIC_STYLES.map((style) => ({ id: style.id, name: `${style.name} — ${style.note}` }));
    const answer = await askPage('animate', documentId, pageSummary(session.open.scene, pageId, { fileName: session.name }), { recipes });
    return answer && answer.band !== 'ask' ? answer.value : null; // Jev's offer band is what a Suggested mark is for
  }, [session.open, session.name, documentId]);
  const pageRecipe = animationTarget ? canvas.recipeOf(animationTarget.page.id) : null;
  const animateBar = animating && !viewing ? (
    <AnimateBar
      suggestRecipe={suggestRecipe}
      target={animationTarget}
      recipe={pageRecipe}
      choices={animationTarget && pageRecipe ? canvas.recipeChoices(animationTarget.page.id, pageRecipe) : null}
      picture={layerPicture}
      playback={playback}
      timing={timeline}
      loop={looping}
      hint={!pickedOnce && !seenGuides.has('animate.pick')}
      drawingPath={drawingPath}
      strip={animationStrip}
      selected={selection}
      // The playhead of THIS page: one left on another page is not shown on this one's ruler.
      playhead={() => {
        const at = canvas.playhead();
        return at && at.id === animationTarget?.page.id ? at : null;
      }}
      onHeight={setBarHeight}
      onClose={() => togglePanel('animate')}
      onTiming={() => setTimeline((was) => !was)}
      onLoop={() => setLooping((was) => !was)}
      onWholePage={() => {
        canvas.preview('', null);
        if (animationTarget) canvas.select([animationTarget.page.id]);
      }}
      onPlay={togglePlay}
      onPause={togglePlay}
      onPlayPage={() => {
        const target = canvas.animationTarget();
        if (target) play(target.page.id);
      }}
      onSeek={(seconds) => {
        const target = canvas.animationTarget();
        if (target) hold(target.page.id, seconds);
      }}
      onRecipe={(styleId, settings, watching) => {
        const target = canvas.animationTarget();
        if (!target) return;
        if (canvas.magicAnimate(target.page.id, styleId, settings) && watching) play(target.page.id);
      }}
      onRemove={() => {
        const target = canvas.animationTarget();
        if (!target) return;
        canvas.stop();
        // None is none: the page's animation and every layer's own, in one step.
        canvas.clearAnimation(target.page.id, true);
      }}
      onEdit={(change, phase) => {
        const target = canvas.animationTarget();
        if (!target?.layer) return;
        // Worked out from each layer's OWN animation, so several picked layers keep their
        // own motions, delays and paths (anim-F3); a change that answers with what it was
        // given leaves that one alone.
        canvas.setLayerAnimation(target.layer.ids, change);
        if (phase) watch(target.page.id, target.layer.id, phase);
      }}
      onTry={(tried) => {
        const target = canvas.animationTarget();
        if (!tried || !target?.layer) canvas.preview('', null);
        else canvas.preview(target.layer.id, tried.animation, tried.hold);
      }}
      onDrawPath={() => {
        // Pressed again while waiting for the drag: a change of mind, so it disarms
        // rather than arming again (QA 2026-09-24, anim-F6).
        if (drawingPath) {
          canvas.cancelPath();
          return;
        }
        const target = canvas.animationTarget();
        if (!target?.layer) return;
        canvas.stop();
        setTool('select');
        setDrawingPath(true);
        canvas.drawPath(target.layer.id, (drawn) => {
          setDrawingPath(false);
          if (drawn) play(target.page.id);
        });
      }}
      onSelect={(id) => canvas.select([id])}
      onWindow={(id, span) => {
        const target = canvas.animationTarget();
        if (target) canvas.setLayerTiming([id], timingFor(span, target.page.length));
      }}
      onDelay={(id, delay) =>
        canvas.setLayerAnimation([id], (own, inherited) => {
          const from = own ?? inherited;
          return from ? { ...from, delay } : own;
        })
      }
      onLength={(seconds) => {
        const target = canvas.animationTarget();
        if (target) canvas.setDuration(target.page.id, seconds);
      }}
      onResizeMotion={(id, phase, resize) => {
        const target = canvas.animationTarget();
        if (!target) return;
        canvas.setLayerAnimation([id], (own, inherited) => {
          const from = own ?? inherited;
          return from ? applyMotionResize(from, phase, resize) : own;
        }, resize.window ? timingFor(resize.window, target.page.length) : undefined);
      }}
    />
  ) : null;

  // A cloud export says how far it has got on the Export button itself, and says once,
  // when it is done, whether it worked.
  const cloudState = cloud.state;
  const exporting = cloudState && (cloudState.status === 'QUEUED' || cloudState.status === 'RENDERING') ? cloudState.progress : null;
  const exportingName = cloudState ? { pdf: 'PDF', jpg: 'JPEG', mp4: 'MP4', gif: 'GIF' }[cloudState.format] : '';
  useEffect(() => {
    if (!cloudState) return;
    const name = { pdf: 'PDF', jpg: 'JPEG', mp4: 'MP4', gif: 'GIF' }[cloudState.format];
    if (cloudState.status === 'READY') setToast(`${name} downloaded.${cloudState.warnings.length ? ` ${cloudState.warnings.join(' ')}` : ''}`);
    else if (cloudState.status === 'FAILED') setToast(cloudState.error ?? `The ${name} export failed.`);
    else if (cloudState.status === 'CANCELLED') setToast(`${name} export cancelled.`);
  }, [cloudState?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!toast || uploading > 0) return;
    const timer = setTimeout(() => setToast(null), Math.max(5000, Math.min(12000, toast.length * 60)));
    return () => clearTimeout(timer);
  }, [toast, uploading]);

  /** What the tool in hand will draw with, as the strip and the panel both offer it. */
  const drawWithProps: DrawWith = {
    fill: style.fill,
    fillInk: style.fillInk,
    fillOn: style.fillOn,
    stroke: style.stroke,
    strokeInk: style.strokeInk,
    weight: style.strokeWeight,
    // A colour picked after None brings the fill or stroke back: None is not a lock
    // (Tarek, 2026-10-01: "if I press None there's no way for me to get it back").
    onFill: (hex, ink) => drawWith({ fill: hex, fillInk: ink, fillOn: true }),
    onFillNone: () => drawWith({ fillOn: false }),
    onStroke: (hex, ink) => drawWith({ stroke: hex, strokeInk: ink, ...(style.strokeWeight > 0 ? {} : { strokeWeight: 1 }) }),
    onStrokeNone: () => drawWith({ strokeWeight: 0 }),
    onWeight: (strokeWeight) => drawWith({ strokeWeight }),
  };

  const hidden = new Set<string>([...(flags.isEnabled('artwork.text') ? [] : ['text']), ...(phone ? PHONE_HIDDEN : [])]);
  /** The tool's options, when it has any: in the shelf, or above the phone's tray. */
  const toolOptions = stripRail ? <ToolStrip rail={tool === 'text' ? { ...stripRail, top: undefined } : stripRail} drawWith={drawWithProps} tool={tool} /> : null;
  /** What the shelf is handed, and the phone's tool tray alike. */
  // One picture or clip chosen: its tools are the side panel's Edit tab.
  const pictureEdit = !viewing && imageTools && nodes.length === 1 && !nodes[0]!.locked && imageTools.picture(nodes[0]!.id) ? <PicturePanelSection id={nodes[0]!.id} host={imageTools} onScreen={setEditScreen} /> : null;
  // …and its Info: what it is, and how it was made when generated (ai/GenerationInfo).
  const pictureInfo = !viewing && imageTools && session.open && nodes.length === 1 && imageTools.picture(nodes[0]!.id)
    ? <GenerationInfo id={nodes[0]!.id} open={session.open} client={realGenerationClient} onUse={recipe => { setGenerationReferenceRequest({ key: crypto.randomUUID(), recipe }); openLibraryAt.current('generate'); }} /> : null;
  pictureTabShown.current = panel === 'format' && (panelTab === 'info' && pictureInfo) ? panelTab : null;
  /** Info on the bar's •••: open the panel on that tab; pressed again, close it. */
  const pictureTab = (tab: 'edit' | 'info') => {
    if (pictureTabShown.current === tab) { setPanel(null); return; }
    setPanel('format'); setPanelTab(tab);
  };
  /** Edit with AI: the picture's tools in the library's Generate slot; pressed again, closed. */
  const editAIOpen = editAI && library && libraryPlace === 'generate' && !!pictureEdit;
  const openEditAI = () => {
    if (editAIOpen) { setEditAI(false); return; }
    setLibraryPlace('generate');
    if (window.innerWidth < 1100) setPanel(null);
    setNavigation('assets');
    setEditAI(true);
  };
  const hasPictureEdit = !!pictureEdit;
  useEffect(() => {
    if (!library || libraryPlace !== 'generate' || !hasPictureEdit) setEditAI(false);
  }, [library, libraryPlace, hasPictureEdit]);
  const dockProps: CreateEditDockProps = {
    cropping, onCrop: (id) => canvas.cropPicture(id),
    onEditPoints: () => {
      if (nodes.length !== 1 || nodes[0]?.type !== 'path' || nodes[0].locked) return;
      const modes = canvas.rail('select')?.top;
      if (modes?.kind === 'buttons') modes.items.find(item => item.id === 'point')?.run({ more: false });
    },
    repeat: repeatActions, tool, textModes: textModes?.kind === 'buttons' ? textModes.items : [], nodes, actions, hiddenTools: hidden,
    selectionModes: selectionModes?.kind === 'buttons' ? selectionModes.items.map(mode => ({
      ...mode, run: () => { setTool('select'); canvas.selectMode(mode.id); },
    })) : [],
    selectItems, combinable: actions.canCombine,
    onArtboard: nodes.length === 1 && !!canvas.selectionPlace()?.board,
    onEditText: id => canvas.editText(id),
    units: session.units,
    fonts: canvas.fontsInDocument(),
    variationRail: nodes.length === 1 && nodes[0]?.type === 'effector' ? canvas.rail('cloner') : null,
    blendRail: nodes.length === 1 && nodes[0]?.type === 'blend' ? canvas.rail('blend') : null,
    onCreateBlend: scene && blendSelection(scene, selection).length > 1 ? () => canvas.createBlend() : undefined,
    onExportSelection: chosenLayers ? () => { setPickedScope('layers'); window.dispatchEvent(new Event('artwork:export')); } : undefined,
    options: toolOptions,
    editingPoints,
    onAddons: openAddons, addonsOpen: library && libraryPlace === 'addons', addonName: addons.heldName,
    imageTools: viewing ? null : imageTools,
    onPicturePanel: openEditAI,
    editOpen: editAIOpen,
    onPictureInfo: pictureInfo ? () => pictureTab('info') : undefined,
    infoOpen: pictureTabShown.current === 'info',
    onTool: setTool, assetsOpen: library, onAssets: () => toggleNavigation('assets'),
    railOf: id => canvas.rail(id), panning, onPan: () => setPanning(value => !value),
    animating, onMode: () => togglePanel('animate'),
    onDuplicate: () => canvas.duplicate(), onDelete: () => canvas.deleteSelection(),
    onAnimate: onArtboard ? () => togglePanel('animate') : undefined,
    onProperties: () => { setPanel('format'); setPanelTab('properties'); },
    onEyedrop: viewing ? undefined : () => eyedropSelection(),
    commenting,
    onComment: () => {
      setCommenting((on) => !on);
      setCommentsOpen(true);
      setPanel(null);
    },
  };
  // Escape leaves full screen once nothing else wants it: an open menu or popover and a selection go first (Tarek, 2 Nov).
  const anythingChosen = nodes.length > 0;
  useEffect(() => {
    if (!bare || presenting) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || anythingChosen || menuIsOpen() || typingIsOnCanvas() || keyBelongsToField(event.target, event.key) || document.querySelector('[role="dialog"]')) return;
      event.preventDefault();
      event.stopPropagation();
      setBare(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [bare, presenting, anythingChosen]);
  /** The selection's bar or a tool's options: the top bar's middle, floating in full screen. */
  const selectionBar = !viewing && !animating && (toolOptions || (tool === 'select' && nodes.length > 0))
    ? toolOptions ?? <div className="context-pill"><SelectionActionsBar {...dockProps} /></div>
    : null;
  const status =
    session.problem ??
    (!session.open ? 'Opening the file…' : uploading > 0 ? (uploadingOf <= 1 ? 'Uploading…' : `Uploading ${uploadingOf - uploading + 1} of ${uploadingOf}…`) : null);
  /**
   * A file that could not be opened (it does not exist, is in another workspace, or never
   * arrived) shows its message and a way back, not a whole editor that answers clicks and
   * does nothing — Layers "Opening…" for ever, Rename failing without a word, the title
   * "Untitled" (QA 2026-09-24, files-doc-F28). The canvas element stays, so a file that
   * arrives late still has one to start on.
   */
  const dead = session.problem !== null && (!session.open || session.stopped);

  return (
    <PressContext.Provider value={proofer}>
    <WorkContext.Provider value={work}>
    <ColourSourcesContext.Provider value={colourSources}>
    <PaintImagesContext.Provider value={{ enabled: nodes.length > 0 && nodes.every((node) => node.type !== 'text'), pictures: () => canvas.filePictures(), videoPictures: () => session.open ? [...assetsMap(session.open.root)].flatMap(([assetId, raw]) => { const asset = raw as import('@flobi/artwork-core').Asset; return asset.kind === 'video' && asset.src ? [{ assetId, name: asset.name, src: asset.src, width: asset.width || 0, height: asset.height || 0, duration: asset.duration }] : []; }) : [], upload: (file) => canvas.uploadPaintPicture(file) }}>
    <div data-bare={bare || undefined} data-animating={!!animateBar && !bare || undefined} data-panning={panning || undefined} className="editor-workspace fixed inset-0 overflow-hidden bg-canvas text-label" style={{ '--tool-cursor': toolCursor(feedback.key), '--workspace-top': `${headerHeight}px`, '--workspace-ruler': `${rulerBand}px`, '--workspace-bottom': `${animateBottom ?? dockRoomBottom}px`, '--dock-room-left': `${bottomRow.left}px`, '--dock-room-right': `${bottomRow.right}px`, '--workspace-animation-height': `${animateBar ? barHeight : 0}px`, '--workspace-leading': `${leading}px`, '--workspace-trailing': `${trailing}px` } as React.CSSProperties}>
      <canvas ref={canvas.canvasRef} data-tool={tool} className="editor-canvas absolute inset-0 h-full w-full touch-none" />
      <ToolCursorGuide canvasRef={canvas.canvasRef} feedback={feedback} />
      {session.open && !bare && <ArtworkGeneration key={`${session.open.documentId}:${session.open.pageId}`} open={session.open} canvas={canvas} selection={selection} readOnly={viewing} client={generationClient} projectId={projectRef.current} panelHost={generationHost} runsHost={runsHost} referenceRequest={generationReferenceRequest} onReferenceHandled={key => setGenerationReferenceRequest(current => current?.key === key ? null : current)} onAddReference={ids => { setGenerationReferenceRequest({ key: crypto.randomUUID(), ids: [...ids] }); openLibraryAt.current('generate'); }} onAddToAgent={id => { const node = scene?.get(id); if (node) addToAgentChat([layerItem(node)]); }} onOpen={() => { openLibraryAt.current('generate'); }} onClose={() => setNavigation(null)} onNotice={setToast} />}
      {/* Other people's pointers and the name on what they are typing in: the collab kit's, over the canvas. */}
      {pageRoom && session.open ? <RemotePeople room={pageRoom} canvas={canvas} pageId={session.open.pageId} /> : null}
      {touring ? null : <GuidePeek introduce={introduce} />}
      {view.rulers && !viewing && <RulerUnits inset={insetRef.current} units={session.units} onUnits={(units) => {
        if (session.open) writeMeta(session.open.root, { units }, LOCAL_ORIGIN);
      }} />}

      {/* The file dialog the Place image command opens; it has nowhere to be seen. */}
      <input
        ref={pickPicture}
        type="file"
        accept="image/*,video/mp4,video/webm,video/quicktime,video/x-m4v"
        multiple
        hidden
        onChange={(event) => {
          // Every picture chosen, as one step (files-doc-F12).
          const files = [...(event.target.files ?? [])];
          if (files.length > 0) {
            void canvas.placePicture(files);
            // Placed from the computer's file dialog: the hints point at My Stuff (lib/hints).
            emitHintSignal('image.placed', { fromDisk: true });
          }
          event.target.value = '';
        }}
      />

      <input
        ref={pickReplacement}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void canvas.replacePicture(replacing.current, file);
          event.target.value = '';
        }}
      />

      {dead && session.noAccess ? (
        /* Taken off the file, never on it, or it is in another of this person's workspaces:
           switched there, else "You need access" with Request access — never a dead end
           (SHARING-MODEL §3.3). */
        <div className="absolute inset-0 z-30 overflow-y-auto bg-page">
          {/* Centred while it fits, scrolled when it does not: All Files is never cut off. */}
          <div className="file-access flex min-h-full flex-col items-center justify-center gap-[16px] px-[16px] pt-[32px] pb-[max(32px,env(safe-area-inset-bottom))]">
            <FileAccess
              documentId={documentId}
              name={session.name || knownName || null}
              mode={theme === 'light' ? 'light' : 'dark'}
              onHome={() => navigate('/')}
              // Let in while waiting: the editor starts over on this file, no reload.
              onGranted={() => onReopen?.()}
            />
          </div>
        </div>
      ) : dead ? (
        <div className="absolute inset-0 z-30 grid place-items-center px-[16px]">
          <div role="alert" className="glass-thick ui-pop flex max-w-[420px] flex-col items-center gap-[14px] rounded-[20px] px-[24px] py-[20px] text-center text-[13px] text-label">
            <p>{session.problem}</p>
            <Button variant="prominent" icon="files" onClick={() => navigate('/')}>
              All Files
            </Button>
          </div>
        </div>
      ) : null}

      {/* Full screen hides the chrome with CSS rather than unmounting it, so dialogs, toasts, pickers and menus keep working. */}
      {!dead && (
        <>
          {/* The scroll edge effect: artwork passes under the toolbar, and this keeps the
              toolbar reading as its own layer when it does. */}
          <div aria-hidden className="scroll-edge-top pointer-events-none absolute inset-x-0 top-0 z-10 h-[72px]" />

          <Toolbar
            onHeight={setHeaderHeight}
            name={titleName}
            onRename={(name) => {
              /**
               * Shown as soon as the server says yes, not when its broadcast of the new
               * name arrives: a broadcast can be lost, and the title stayed on the old name
               * however many renames succeeded (QA 2026-09-24, files-doc-F10). Dropped as
               * soon as the file's own name moves on.
               */
              // And shown at once, before the server answers: waiting for it, the old name
              // came back for a moment after Return (QA 2026-10-05, ART-DOC-03). A refusal
              // puts the old name back and says so.
              const from = session.name;
              setRenamed({ from, to: name });
              artworkCmd<{ name?: string }>('artwork.doc.rename', { documentId, name })
                .then((doc) => setRenamed((now) => (now?.from === from && now.to === name ? { from, to: typeof doc?.name === 'string' ? doc.name : name } : now)))
                .catch(() => {
                  setRenamed((now) => (now?.from === from && now.to === name ? null : now));
                  setToast('That name could not be saved.');
                });
            }}
            documentMenu={documentMenu}
            onDocumentMenu={() => void history.refresh()}
            onShare={shareByButton}
            bell={<Notifications toolbar />}
            inspecting={formatOut}
            onInspect={() => togglePanel('format')}
            onPresent={() => { window.open(presentUrl(documentId, hereBoard?.id ?? null), '_blank'); }}
            center={bare ? null : selectionBar}
            settings={
              settingsOpen ? (
                <DocumentSettings
                  units={session.units}
                  resolution={session.resolution}
                  grid={{ step: session.gridStep, divisions: session.gridDivisions }}
                  colorMode={session.colorMode}
                  press={proofer?.profileName ?? null}
                  onColorMode={(colorMode) => {
                    if (session.open) writeMeta(session.open.root, { colorMode }, LOCAL_ORIGIN);
                  }}
                  onUnits={(units) => {
                    if (session.open) writeMeta(session.open.root, { units }, LOCAL_ORIGIN);
                  }}
                  onResolution={(resolution) => {
                    if (session.open) writeMeta(session.open.root, { resolution }, LOCAL_ORIGIN);
                  }}
                  onGrid={(patch) => {
                    if (session.open) writeMeta(session.open.root, patch.step !== undefined ? { gridStep: patch.step } : { gridDivisions: patch.divisions }, LOCAL_ORIGIN);
                  }}
                />
              ) : null
            }
            onSettingsClose={() => setSettingsOpen(false)}
            exportOptions={exportOptions}
            zoomMenu={zoomMenu}
            zoom={zoom}
            room={pageRoom}
            follow={follow}
            readOnly={viewing}
            tool={mark}
            shapeIcon={KINDS.find((k) => k.id === shape)?.icon ?? 'square'}
            onTool={(id: ToolMark['id']) => {
              // The shape mark picks up whichever shape was last in hand; its five kinds
              // are chosen in the side panel, so the toolbar only gets you into the family.
              setTool(id === 'shape' ? shape : (id as ToolId));

            }}
            hiddenTools={hidden}
            onFiles={() => navigate('/')}
            adding={library}
            onAdd={() => { if (!library) emitHintSignal('library.opened', { viaButton: true }); toggleNavigation('assets'); }}
            onUndo={() => session.open?.undo.undo()}
            onRedo={() => session.open?.undo.redo()}
            canUndo={undoState.canUndo}
            canRedo={undoState.canRedo}
            animating={animating}
            onAnimate={() => togglePanel('animate')}
            // The add-on HELD, not the one the panel shows (QA 2026-09-24, library-addons-F16).
            addonInHand={tool === 'addon' ? (addons.heldName ?? 'Add-on') : null}
            onAddons={openAddons}
            onZoomBy={(factor) => canvas.zoomBy(factor)}
            onFit={() => canvas.fit()}
            navSlot={setRunsHost}
            // Past a phone the zoom pill is only a run's bar (Tarek, 1 Nov): zoom is in View and on the keys, the hand under Select's arrow.
            foldNav={!phone || bottomRow.folded}
            panning={panning}
            onPan={() => setPanning(value => !value)}
            exporting={exporting}
            phone={phone}
            // Shift and Option for a finger, on a touch screen of any size (touchInput.ts).
            navExtra={COARSE && !viewing ? <HeldKeys /> : undefined}
            // On a phone the bottom is the tool tray's, drawn below; the dock keeps none. While
            // animating it keeps none either: the Animate bar's own X goes back (Tarek, 2 Nov 2026).
            dock={phone || animating ? <></> : <CreateEditDock {...dockProps} />}
          />
          {/* In full screen the same bar floats over the canvas, since the top bar is hidden. */}
          {bare && !phone && selectionBar ? <div className="workspace-context">{selectionBar}</div> : null}
          {/* A viewer has no tools, on a phone as in the shelf (`readOnly` hides it). */}
          {phone && !animating && !viewing && !bare ? (
            <PhoneToolTray {...dockProps} strip={toolOptions} onProperties={() => { if (panel !== 'format') togglePanel('format'); }} />
          ) : null}

          {!viewing && !phone && <div className="workspace-navigation workspace-library-rail pointer-events-auto absolute z-20 flex overflow-hidden" style={{ width: LIBRARY_RAIL_WIDTH }} onKeyDown={event => {
            if (event.key === 'Escape' && !event.defaultPrevented && event.currentTarget.contains(event.target as Node)) setNavigation(null);
          }}>
            <LibraryRail chosen={layersOpen ? 'layers' : library ? libraryPlace : ''} onChoose={place => { openLibraryAt.current(place); }} onLayers={() => {
              if (window.innerWidth < 1100) setPanel(null);
              setNavigation('layers');
            }} onUpload={libraryActions.upload} />
          </div>}

          {library ? phoneSheet(phone, 'Assets', () => setNavigation(null), 'tall',
            <AddonsContext.Provider value={addons}>
              {/* On a phone the rail stands in the sheet, across its top (Artwork A). */}
              <Library open panelOnly={!phone} onLayers={() => setNavigation('layers')} onClose={() => setNavigation(null)} chosen={libraryPlace} onChoose={setLibraryPlace} actions={libraryActions} generationHost={setGenerationHost}
                override={editAIOpen ? { title: editScreen?.title ?? 'Edit with AI', back: editScreen?.back, backLabel: 'Edit with AI', node: <div className="px-[14px] py-[10px]">{pictureEdit}</div> } : null} />
            </AddonsContext.Provider>,
          ) : null}

          {commenting && pinsShown ? (
            <div
              ref={commentLayer}
              aria-label="Click to comment"
              className="absolute bottom-0 z-[15] cursor-crosshair"
              style={{ top: headerHeight, left: leading, right: trailing }}
              onPointerDown={(event) => {
                if (event.button !== 0) return;
                event.preventDefault();
                dropPin({ x: event.clientX, y: event.clientY });
              }}
            />
          ) : null}
          {pinsShown && pageId ? (
            <div ref={pinLayer} className="pointer-events-none fixed inset-0 z-[16]" style={{ clipPath: `inset(${headerHeight}px ${trailing}px 0 ${leading}px)` }}>
              {(comments.threads ?? [])
                .filter((thread) => thread.anchor.pageId === pageId && (!thread.resolved || thread.id === chosenThread))
                .map((thread) => {
                  const at = canvas.screenAt(pinPoint(thread.anchor, worldBox));
                  return (
                    <CommentPin
                      key={thread.id}
                      x={at.x}
                      y={at.y}
                      label={String(pinNumbers.get(thread.id) ?? '')}
                      resolved={thread.resolved}
                      chosen={thread.id === chosenThread}
                      onPress={() => {
                        setCommentsOpen(true);
                        setPanel(null);
                        setChosenThread(thread.id === chosenThread ? null : thread.id);
                      }}
                    />
                  );
                })}
              {draftPin && draftPin.pageId === pageId ? (() => {
                const at = canvas.screenAt(pinPoint(draftPin, worldBox));
                return <CommentPin x={at.x} y={at.y} label="" chosen draft />;
              })() : null}
            </div>
          ) : null}
          {commentsOut ? phoneSheet(phone, 'Comments', () => { setCommentsOpen(false); setCommenting(false); setDraftPin(null); }, 'mid',
            <CommentsPanel
              className="absolute right-[8px] top-[60px] z-20 max-h-[calc(100%-120px)] rounded-[18px] pointer-coarse:top-[68px]"
              style={{ width: PANEL_WIDTH }}
              threads={comments.threads}
              failed={comments.failed}
              canComment={comments.canComment}
              chosen={chosenThread}
              numbers={pinNumbers}
              draft={draftPin !== null}
              people={commentPeople}
              onChoose={setChosenThread}
              onPostDraft={postDraft}
              onCancelDraft={() => setDraftPin(null)}
              onReply={(threadId, body, mentions) => void comments.reply(threadId, body, mentions)}
              onResolve={(threadId, resolved) => {
                void comments.resolve(threadId, resolved);
                if (resolved && chosenThread === threadId) setChosenThread(null);
              }}
              onDelete={(threadId, commentId) => {
                void comments.remove(threadId, commentId);
                if (threadId === commentId) setChosenThread(null);
              }}
              onRetry={() => void comments.refresh()}
              onClose={() => {
                setCommentsOpen(false);
                setCommenting(false);
                setDraftPin(null);
              }}
            />,
          ) : null}

          {!viewing && navigation === 'versions' && !(phone && chosenVersion) ? phoneSheet(phone, 'Version history', () => { setChosenVersion(null); setNavigation(null); }, 'tall',
            <VersionsPanel
              versions={history.versions}
              more={history.more}
              failed={history.failed}
              chosen={chosenVersion?.id ?? null}
              onChoose={setChosenVersion}
              onRename={(version) => setNaming({ version })}
              onMore={() => void history.loadMore()}
              onRetry={() => void history.refresh()}
              onClose={() => {
                setChosenVersion(null);
                setNavigation(null);
              }}
            />,
          ) : null}
          {!viewing && navigation === 'versions' && chosenVersion ? (
            <VersionPreview
              version={history.versions?.find((one) => one.id === chosenVersion.id) ?? chosenVersion}
              top={headerHeight}
              left={leading}
              right={trailing}
              busy={restoring}
              onRestore={() => restoreTo(chosenVersion)}
              onCopy={() => copyFrom(chosenVersion)}
              onDone={() => setChosenVersion(null)}
            />
          ) : null}
          {naming ? <NameVersionDialog title={naming.version ? 'Name this version' : 'Name current version'} initial={naming.version?.name ?? ''} onSave={saveName} onCancel={cancelNaming} /> : null}
          {templateDraft && <SaveTemplateDialog {...templateDraft} onClose={() => setTemplateDraft(null)} onSaved={setToast} />}

          {layersOpen ? phoneSheet(phone, 'Layers', () => setNavigation(null), 'mid',
            <NavigationPanel onClose={() => setNavigation(null)}>
              {
                session.open ? <FocusedArtboardPanel
                  scene={session.open.scene}
                  selection={selection}
                  version={sceneVersion}
                  boards={boards}
                  onFocus={id => canvas.focusArtboard(id)}
                  onOrder={order => {
                    const open = session.open; if (!open) return;
                    const ids = new Set(order);
                    const ops = boardSlotMoves(open.scene.childrenOf('root'), id => ids.has(id), order).map(({ id, position }) => ({ op: 'move', id, parentId: 'root', position, force: true }));
                    open.undo.stopCapturing();
                    applyOps(open.page, ops, { scene: open.scene, origin: LOCAL_ORIGIN });
                    open.undo.stopCapturing();
                  }}
                  onAdd={(preset, draft) => canvas.addArtboard(preset, false, draft)}
                  onOverview={() => canvas.fit()}
                  onSaveTemplate={saveArtboardTemplate}
                  columns={grid.columns ?? columnsFor(canvas.exportBoards('all').length)}
                  gap={grid.gap}
                  onArrange={(columns, gap) => { keepGrid({ columns, gap }); canvas.arrangeArtboards({ columns, gap }); }}
                  onSelect={id => canvas.select([id])}
                  onSelectIds={ids => canvas.select(ids)}
                  onGroup={id => { if (!selection.includes(id)) canvas.select([id]); canvas.group(); }}
                  onUngroup={id => { if (!selection.includes(id)) canvas.select([id]); canvas.ungroup(); }}
                  onUpdate={(id, props) => canvas.update(id, props)}
                  onMove={(id, target) => canvas.move(id, target)}
                  actions={layerActions}
                  onAddToChat={(id) => { const node = scene?.get(id); if (node) addToAgentChat([layerItem(node)]); }}
                  onEditText={(id) => canvas.editText(id)}
                  units={session.units}
                  openEffects={effectsOn}
                  getShader={id => canvas.getShader(id)}
                  onEffect={(id, index, props) => canvas.setEffect(id, index, props)}
                  onRemoveEffect={(id, index) => canvas.removeEffect(id, index)}
                  onMoveEffect={(id, index, to) => canvas.moveEffect(id, index, to)}
                /> : <p className="p-[14px] text-[12px] text-label-2">Opening…</p>
              }
            </NavigationPanel>,
          ) : null}

          {panelOut ? phoneSheet(phone, 'Properties', () => setPanel(null), 'half',
            <SidePanel
              onClose={() => setPanel(null)}
              tool={tool}
              toolLabel={toolLabel}
              rail={rail}
              drawWith={drawWithProps}
              nodes={nodes}
              canAlign={nodes.length > 1 || !!canvas.selectionPlace()?.board}
              anchor={canvas.anchor()}
              units={session.units}
              actions={actions}
              fonts={canvas.fontsInDocument()}
              repeat={repeatActions}
              variationRail={nodes.length === 1 && nodes[0]?.type === 'effector' ? canvas.rail('cloner') : null}
              blendRail={nodes.length === 1 && nodes[0]?.type === 'blend' ? canvas.rail('blend') : null}
              tab={panelTab}
              onTab={setPanelTab}
              info={pictureInfo}
              page={{
                artboard: (() => {
                  const current = boards.find((board) => board.current) ?? boards[0];
                  return (current && scene?.get(current.id)) || null;
                })(),
                artboards: boards,
                units: session.units,
                onUpdate: (id, props) => canvas.update(id, props),
                onFocus: (id) => canvas.focusArtboard(id),
                onAdd: (preset) => canvas.addArtboard(preset),
              }}
            />,
          ) : null}

          {animateBar ? (
            <div data-flobi-bottom-bar={phone || undefined} className="workspace-animation pointer-events-none absolute inset-x-0 bottom-0 z-30">
              {animateBar}
            </div>
          ) : null}

          {shortcutsOpen ? <ShortcutsSheet groups={shortcutGroups({ text: flags.isEnabled('artwork.text'), comments: !viewing })} onClose={closeShortcuts} /> : null}

          {deleting ? (
            <ConfirmAlert
              title={`Move “${session.name || 'Untitled'}” to Trash?`}
              message="It leaves every list, for everyone. You can restore it from Trash for 30 days; then it is deleted for good."
              action="Move to Trash"
              onCancel={() => setDeleting(false)}
              onConfirm={() => {
                setDeleting(false);
                artworkCmd('artwork.doc.delete', { documentId })
                  .then(() => navigate('/'))
                  .catch(() => setToast('This file could not be moved to Trash.'));
              }}
            />
          ) : null}

          {startFailed ? (
            <div role="alert" className="glass-thick ui-pop pointer-events-auto absolute left-1/2 top-1/2 z-30 flex max-w-[420px] -translate-x-1/2 -translate-y-1/2 items-center gap-[10px] rounded-full py-[6px] pl-[14px] pr-[6px] text-[13px] text-label">
              <span className="min-w-0">The canvas couldn’t start. Check your connection.</span>
              <Button
                size="small"
                variant="prominent"
                onClick={() => {
                  const retry = startFailed;
                  setStartFailed(null);
                  retry();
                }}
              >
                Try Again
              </Button>
            </div>
          ) : null}

          {/* Messages that have something to say for a moment, under the toolbar. */}
          {/* An online export says so here too, with its Cancel: the bubble showed only
              with a message in it, so the Cancel inside it never appeared (ART-EXP-15). */}
          {status || toast || localExport || exporting !== null ? (
            <div role="status" style={{ top: headerHeight + 8 }} className="glass-thick ui-pop pointer-events-auto absolute left-1/2 z-30 flex w-max max-w-[min(480px,calc(100%-24px))] -translate-x-1/2 items-center gap-[10px] rounded-[16px] py-[8px] pl-[14px] pr-[8px] text-[13px] text-label">
              {(localExport || status) && !session.problem ? <Spinner size={12} className="text-label-2" /> : null}
              <span className="min-w-0 break-words leading-[1.45]">{status ?? localExport ?? toast ?? (cloudState?.status === 'QUEUED' ? `${exportingName} waiting for another export to finish…` : `Exporting ${exportingName}… ${exporting}%`)}</span>
              {(localExport || exporting !== null) && !status ? (
                <Button size="small" onClick={exportOptions.onCancel}>
                  Cancel
                </Button>
              ) : !status && noticeAction ? (
                <Button
                  size="small"
                  variant="prominent"
                  onClick={() => {
                    noticeAction.run();
                    setToast(null);
                  }}
                >
                  {noticeAction.label}
                </Button>
              ) : !status ? (
                <Button size="small" variant="borderless" onClick={() => setToast(null)}>
                  Close
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      {/* Full screen leaves by F or four fingers; a touch screen shows the way out too. */}
      {presenting ? <PresentStage canvas={canvas} ready={fileOpen} onShow={id => play(id)} /> : null}
      {bare && !presenting && !dead ? (
        <button type="button" className="exit-full-screen glass-thick" onClick={() => setBare(false)}>
          <Icon name="close" size={14} />
          Exit full screen
        </button>
      ) : null}

      <QuickMenu
        at={quickAt}
        label={editingPoints && rail?.pointEditing?.total ? 'Point actions' : 'Canvas actions'}
        onClose={() => setQuickAt(null)}
        items={quickAt ? canvasMenu({
          nodes, canvas, at: quickAt, ...undoState, pointRail: editingPoints ? rail : null,
          artboard: quickAt.artboardId ? scene?.get(quickAt.artboardId) : undefined,
          undo: () => session.open?.undo.undo(),
          redo: () => session.open?.undo.redo(),
          textEnabled: flags.isEnabled('artwork.text'),
          phone,
          tool: setTool,
          properties: () => { setBare(presenting); setPanel('format'); setPanelTab('properties'); },
          info: pictureInfo ? () => pictureTab('info') : undefined,
          layers: () => { setBare(presenting); setNavigation('layers'); },
          assets: () => { setBare(presenting); setNavigation('assets'); },
          image: () => pickPicture.current?.click(),
          replaceImage,
          addReference: scene && selectedReferenceSources(scene, canvas, nodes.map(node => node.id)).length ? ids => {
            setGenerationReferenceRequest({ key: crypto.randomUUID(), ids: [...ids] });
            openLibraryAt.current('generate');
          } : undefined,
          exportSelection: () => { setPickedScope('layers'); window.dispatchEvent(new Event('artwork:export')); },
          saveTemplate: saveArtboardTemplate,
          flowLogo: appLogo('flow'),
          sendToFlow: session.open ? (id) => {
            const open = session.open;
            if (!open) return;
            void sendArtboardToFlow(flowSourceOf(open), id).then(({ leftOut }) => { if (leftOut > 0) setToast(leftOutWords(leftOut)); }).catch((error: Error) => setToast(error.message || 'It could not be sent to Flow. Try again.'));
          } : undefined,
          node: (id) => scene?.get(id) ?? undefined,
          clipboard,
          addToChat: (chosen) => addToAgentChat(chosen.map(layerItem)),
        }) : []}
      />
    </div>
    </PaintImagesContext.Provider>
    </ColourSourcesContext.Provider>
    </WorkContext.Provider>
    </PressContext.Provider>
  );
}

/** A drawer as it stands: beside the canvas on a laptop, a bottom sheet on a phone (Artwork A). */
function phoneSheet(phone: boolean, label: string, onClose: () => void, size: 'tall' | 'mid' | 'half', panel: React.ReactNode) {
  return phone ? <PhoneSheet label={label} onClose={onClose} size={size}>{panel}</PhoneSheet> : panel;
}

/**
 * Choosing a colour is choosing to see it: a fill colour turns the fill on, and an
 * outline colour gives an outline of no weight a weight.
 */
function settled(patch: Partial<EditorStyle>, from: EditorStyle): Partial<EditorStyle> {
  const full = { ...patch };
  if (patch.fill !== undefined && patch.fillOn === undefined) full.fillOn = true;
  // A colour given without its ink stands for no ink: the old one would print instead.
  if (patch.fill !== undefined && !('fillInk' in patch)) full.fillInk = undefined;
  if (patch.stroke !== undefined && !('strokeInk' in patch)) full.strokeInk = undefined;
  if (patch.stroke !== undefined && (patch.strokeWeight ?? from.strokeWeight) <= 0) full.strokeWeight = 1;
  // Opacity is a share: past 100% every tool's create was refused (inspector-numbers-F9).
  if (full.opacity !== undefined) full.opacity = Math.min(1, Math.max(0, full.opacity));
  return full;
}

/** Whether there is anything to undo or redo, kept in step with the undo manager. */
function useUndoState(undo: { undoStack: unknown[]; redoStack: unknown[]; on(event: string, fn: () => void): void; off(event: string, fn: () => void): void } | null) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!undo) return;
    const bump = () => setTick((tick) => tick + 1);
    undo.on('stack-item-added', bump);
    undo.on('stack-item-popped', bump);
    undo.on('stack-cleared', bump);
    return () => {
      undo.off('stack-item-added', bump);
      undo.off('stack-item-popped', bump);
      undo.off('stack-cleared', bump);
    };
  }, [undo]);
  return { canUndo: (undo?.undoStack.length ?? 0) > 0, canRedo: (undo?.redoStack.length ?? 0) > 0 };
}

/** The Select tool's ways of growing a choice, by the names a Select menu gives them. */
const REACH_NAMES: Record<string, string> = {
  grow: 'Grow selection',
  shrink: 'Shrink selection',
  same: 'Select same fill',
  invert: 'Invert selection',
  mirror: 'Choose across the axis',
};
